import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/Toast';
import { Textarea } from '@/components/ui/textarea';
import { 
  Settings, Scale, Printer, Receipt, Store, Save, 
  Monitor, Wifi, Database, Shield, Bell, X, Check,
  Building2, FileText, CreditCard, Upload, Key, Globe, Link, ExternalLink, CheckCircle, XCircle, Loader2,
  Download, Smartphone, Tablet, Laptop, Import, FileSpreadsheet, Server, RefreshCw, AlertTriangle, Trash2,
  Eye, EyeOff
} from 'lucide-react';
import { api } from '@/lib/api';
import { InstallPWA } from '@/components/InstallPWA';
import { CatalogMediaUploader } from '@/components/CatalogMediaUploader';
import { PaymentMethodsSettings } from '@/components/PaymentMethodsSettings';

export const ConfiguracoesPage = () => {
  const toast = useToast();
  const { user } = useAuth();
  
  // Configurações do Catálogo Online
  const [catalogConfig, setCatalogConfig] = useState({
    slug: '',
    enabled: false,
    url: null,
    whatsapp: '',
    phone: '',
    address: '',
    instagram: '',
    description: '',
    delivery_enabled: false,
    delivery_fee: 0,
    min_order_value: 0
  });
  const [newSlug, setNewSlug] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [savingCatalog, setSavingCatalog] = useState(false);
  
  // Configurações da Balança
  const [balancaConfig, setBalancaConfig] = useState({
    habilitada: false,
    tipo: 'generica',
    baud_rate: 9600,
    auto_conectar: true,
    // Configurações de código de barras (local)
    prefixoPesado: '2',
    formatoCodigo: 'padrao',
    casasDecimais: 3
  });
  const [savingBalanca, setSavingBalanca] = useState(false);
  const [testingBalanca, setTestingBalanca] = useState(false);
  
  // Configurações do PDV
  const [pdvConfig, setPdvConfig] = useState({
    emitirNfceAutomatico: true,
    imprimirCupom: true,
    solicitarCliente: false,
    mostrarEstoque: true,
    permitirVendaNegativa: false,
    descricaoResumida: false
  });
  
  // Configurações da Empresa
  const [empresaConfig, setEmpresaConfig] = useState({
    razaoSocial: '',
    nomeFantasia: '',
    cnpj: '',
    inscricaoEstadual: '',
    inscricaoMunicipal: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: 'SP',
    cep: '',
    codigoMunicipio: '',
    telefone: '',
    email: ''
  });
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [savingEmpresa, setSavingEmpresa] = useState(false);
  
  // Configurações Fiscais Completas
  const [fiscalConfig, setFiscalConfig] = useState({
    // Regime e Ambiente
    regimeTributario: '1', // 1-Simples Nacional, 2-Simples Excesso, 3-Normal
    uf: 'SP', // UF da empresa
    
    // Certificado Digital
    certificadoNome: '',
    certificadoValidade: '',
    certificadoStatus: 'não_configurado', // não_configurado, válido, expirado, expirando
    
    // NFC-e
    habilitarNfce: true,
    serieNfce: '1',
    proximoNumeroNfce: '1',
    ambienteNfce: '2', // 1-Produção, 2-Homologação
    csc: '',
    idCsc: '',
    modeloNfce: '65',
    
    // NF-e
    habilitarNfe: false,
    serieNfe: '1',
    proximoNumeroNfe: '1',
    ambienteNfe: '2', // 1-Produção, 2-Homologação
    modeloNfe: '55',

    // Responsável Técnico (NT 2018.005 - opcional, mas exigido em alguns estados)
    respTecCnpj: '',
    respTecContato: '',
    respTecEmail: '',
    respTecFone: '',
    
    // Impressão/DANFE
    formatoDanfe: '1', // 1-Retrato, 2-Paisagem
    imprimirAutomatico: true,
    impressoraPadrao: '',
    logoDanfe: true,
    
    // Servidor SEFAZ
    servidorNfce: '',
    servidorNfe: '',
    timeout: '30',
    
    // Contingência
    modoContingencia: false,
    tipoContingencia: 'off', // off, SCAN, DPEC, EPEC, FS-DA
    justificativaContingencia: ''
  });
  
  // Estado do certificado digital
  const [uploadingCert, setUploadingCert] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [certPassword, setCertPassword] = useState('');
  
  // Tabs internas da seção fiscal
  const [fiscalTab, setFiscalTab] = useState('geral');
  
  // Status SEFAZ NF-e
  const [statusSefazNfe, setStatusSefazNfe] = useState(null);
  const [loadingStatusNfe, setLoadingStatusNfe] = useState(false);

  // Certificado Digital
  const [certificado, setCertificado] = useState(null);
  const [certificadoSenha, setCertificadoSenha] = useState('');

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('empresa');

  // Migração de Produtos
  const [migracaoConfig, setMigracaoConfig] = useState({
    host: 'localhost',
    port: '3050',
    database: 'C:\\SigLight\\Database\\BANCO.GDB',
    user: 'SYSDBA',
    password: 'masterkey'
  });
  const [migracaoStatus, setMigracaoStatus] = useState(null);
  const [migrando, setMigrando] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importandoCsv, setImportandoCsv] = useState(false);

  // Configurações de Pagamentos
  const [paymentConfig, setPaymentConfig] = useState({
    mercadopago_habilitado: false,
    mercadopago_access_token: '',
    mercadopago_public_key: '',
    pagseguro_habilitado: false,
    pagseguro_token: '',
    pix_timeout_minutos: 30
  });
  const [showMpToken, setShowMpToken] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);

  // Estados para Diagnóstico de Números NFC-e
  const [analiseNumeros, setAnaliseNumeros] = useState(null);
  const [loadingAnalise, setLoadingAnalise] = useState(false);
  const [corrigindoNumeros, setCorrigindoNumeros] = useState(false);
  const [limpandoSimulacao, setLimpandoSimulacao] = useState(false);
  const [novoProximoNumero, setNovoProximoNumero] = useState('');

  // Carregar configurações salvas
  useEffect(() => {
    const savedBalanca = localStorage.getItem('config_balanca');
    const savedPdv = localStorage.getItem('config_pdv');
    const savedEmpresa = localStorage.getItem('config_empresa');
    const savedFiscal = localStorage.getItem('config_fiscal');
    
    if (savedBalanca) setBalancaConfig(prev => ({...prev, ...JSON.parse(savedBalanca)}));
    if (savedPdv) setPdvConfig(prev => ({...prev, ...JSON.parse(savedPdv)}));
    if (savedEmpresa) setEmpresaConfig(JSON.parse(savedEmpresa));
    if (savedFiscal) setFiscalConfig(JSON.parse(savedFiscal));
    
    // Carregar status do certificado
    loadCertificado();
    // Carregar configurações do catálogo
    loadCatalogConfig();
    // Carregar configurações da balança do banco
    loadBalancaConfig();
    // Carregar configurações de pagamento
    loadPaymentConfig();
    // Carregar dados da empresa do banco
    loadEmpresaConfig();
    // Carregar configurações fiscais do banco
    loadFiscalConfig();
  }, []);

  // Carregar configurações fiscais do banco de dados
  const loadFiscalConfig = async () => {
    try {
      const response = await api.get('/settings/fiscal');
      if (response.data && Object.keys(response.data).length > 0) {
        const data = response.data;
        setFiscalConfig(prev => ({
          ...prev,
          regimeTributario: data.regime_tributario || prev.regimeTributario,
          uf: data.uf || prev.uf,
          habilitarNfce: data.habilitar_nfce !== undefined ? data.habilitar_nfce : prev.habilitarNfce,
          serieNfce: data.serie_nfce || prev.serieNfce,
          proximoNumeroNfce: data.proximo_numero || prev.proximoNumeroNfce,
          ambienteNfce: data.ambiente_nfce === 'producao' ? '1' : '2',
          csc: data.csc || prev.csc,
          idCsc: data.id_csc || data.csc_id || prev.idCsc,
          habilitarNfe: data.habilitar_nfe !== undefined ? data.habilitar_nfe : prev.habilitarNfe,
          serieNfe: data.serie_nfe || prev.serieNfe,
          proximoNumeroNfe: data.proximo_numero_nfe || prev.proximoNumeroNfe,
          ambienteNfe: data.ambiente_nfe === 'producao' ? '1' : '2',
          // Responsável Técnico (NT 2018.005 - opcional)
          respTecCnpj: data.resp_tec_cnpj || prev.respTecCnpj || '',
          respTecContato: data.resp_tec_contato || prev.respTecContato || '',
          respTecEmail: data.resp_tec_email || prev.respTecEmail || '',
          respTecFone: data.resp_tec_fone || prev.respTecFone || '',
        }));
      }
    } catch (error) {
      console.log('Configurações fiscais não encontradas no banco');
    }
  };

  // Carregar dados da empresa do banco de dados
  const loadEmpresaConfig = async () => {
    try {
      const response = await api.get('/settings/company');
      if (response.data && Object.keys(response.data).length > 0) {
        setEmpresaConfig(prev => ({
          ...prev,
          razaoSocial: response.data.razao_social || '',
          nomeFantasia: response.data.nome_fantasia || '',
          cnpj: response.data.cnpj || '',
          inscricaoEstadual: response.data.inscricao_estadual || response.data.ie || '',
          inscricaoMunicipal: response.data.inscricao_municipal || '',
          endereco: response.data.endereco || response.data.logradouro || '',
          numero: response.data.numero || '',
          complemento: response.data.complemento || '',
          bairro: response.data.bairro || '',
          cidade: response.data.cidade || '',
          uf: response.data.uf || 'SP',
          cep: response.data.cep || '',
          codigoMunicipio: response.data.cod_municipio || response.data.codigo_municipio || '',
          telefone: response.data.telefone || '',
          email: response.data.email || ''
        }));
      }
    } catch (error) {
      console.log('Configuração da empresa não encontrada no banco');
    }
  };

  // Buscar dados do CNPJ via API
  const buscarCnpj = async (cnpj) => {
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      toast.warning('CNPJ deve ter 14 dígitos');
      return;
    }
    
    setBuscandoCnpj(true);
    try {
      const response = await api.get(`/consulta/cnpj/${cnpjLimpo}`);
      if (response.data && response.data.success && response.data.data) {
        const data = response.data.data;
        setEmpresaConfig(prev => ({
          ...prev,
          razaoSocial: data.razao_social || prev.razaoSocial,
          nomeFantasia: data.nome_fantasia || prev.nomeFantasia,
          cnpj: data.cnpj || cnpj,
          endereco: data.logradouro || prev.endereco,
          numero: data.numero || prev.numero,
          complemento: data.complemento || prev.complemento,
          bairro: data.bairro || prev.bairro,
          cidade: data.cidade || prev.cidade,
          uf: data.uf || prev.uf,
          cep: data.cep ? data.cep.replace(/\D/g, '') : prev.cep,
          codigoMunicipio: data.cod_municipio || prev.codigoMunicipio,
          telefone: data.telefone || prev.telefone,
          email: data.email || prev.email
        }));
        toast.success('Dados do CNPJ carregados com sucesso!');
      } else {
        toast.error('Não foi possível carregar os dados do CNPJ');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao consultar CNPJ');
    } finally {
      setBuscandoCnpj(false);
    }
  };

  // Salvar dados da empresa no banco
  const saveEmpresaConfig = async () => {
    setSavingEmpresa(true);
    try {
      await api.post('/settings/company', {
        razao_social: empresaConfig.razaoSocial,
        nome_fantasia: empresaConfig.nomeFantasia,
        cnpj: empresaConfig.cnpj,
        inscricao_estadual: empresaConfig.inscricaoEstadual,
        ie: empresaConfig.inscricaoEstadual,
        inscricao_municipal: empresaConfig.inscricaoMunicipal,
        endereco: empresaConfig.endereco,
        logradouro: empresaConfig.endereco,
        numero: empresaConfig.numero,
        complemento: empresaConfig.complemento,
        bairro: empresaConfig.bairro,
        cidade: empresaConfig.cidade,
        uf: empresaConfig.uf,
        cep: empresaConfig.cep,
        cod_municipio: empresaConfig.codigoMunicipio,
        telefone: empresaConfig.telefone,
        email: empresaConfig.email
      });
      // Também salvar no localStorage como backup
      localStorage.setItem('config_empresa', JSON.stringify(empresaConfig));
      toast.success('Dados da empresa salvos com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar dados da empresa');
    } finally {
      setSavingEmpresa(false);
    }
  };

  const loadPaymentConfig = async () => {
    try {
      const response = await api.get('/settings/pagamento');
      if (response.data) {
        setPaymentConfig(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.log('Configuração de pagamento não encontrada');
    }
  };

  const savePaymentConfig = async () => {
    setSavingPayment(true);
    try {
      await api.post('/settings/pagamento', paymentConfig);
      toast.success('Configurações de pagamento salvas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações de pagamento');
    } finally {
      setSavingPayment(false);
    }
  };

  const loadBalancaConfig = async () => {
    try {
      const response = await api.get('/settings/balanca');
      setBalancaConfig(prev => ({
        ...prev,
        habilitada: response.data.habilitada,
        tipo: response.data.tipo,
        baud_rate: response.data.baud_rate,
        auto_conectar: response.data.auto_conectar
      }));
    } catch (error) {
      console.log('Configuração de balança não encontrada');
    }
  };

  const saveBalancaConfig = async () => {
    setSavingBalanca(true);
    try {
      await api.post('/settings/balanca', {
        habilitada: balancaConfig.habilitada,
        tipo: balancaConfig.tipo,
        baud_rate: balancaConfig.baud_rate,
        auto_conectar: balancaConfig.auto_conectar
      });
      // Salvar configurações locais
      localStorage.setItem('config_balanca', JSON.stringify(balancaConfig));
      toast.success('Configurações da balança salvas!');
    } catch (error) {
      toast.error('Erro ao salvar configurações da balança');
    } finally {
      setSavingBalanca(false);
    }
  };

  const testBalancaConnection = async () => {
    if (!('serial' in navigator)) {
      toast.error('Web Serial API não suportada. Use Chrome ou Edge.');
      return;
    }
    
    setTestingBalanca(true);
    try {
      // Solicita seleção de porta e tenta conectar
      const port = await navigator.serial.requestPort();
      await port.open({
        baudRate: balancaConfig.baud_rate,
        dataBits: 8,
        stopBits: 1,
        parity: 'none'
      });
      
      toast.success('Balança conectada com sucesso! A porta foi autorizada para reconexão automática.');
      await port.close();
    } catch (error) {
      if (error.name === 'NotFoundError') {
        toast.warning('Nenhuma porta selecionada');
      } else {
        toast.error('Erro ao conectar: ' + error.message);
      }
    } finally {
      setTestingBalanca(false);
    }
  };

  const loadCatalogConfig = async () => {
    try {
      const response = await api.get('/catalogo/config');
      setCatalogConfig(response.data);
      if (response.data.slug) {
        setNewSlug(response.data.slug);
      }
    } catch (error) {
      console.log('Catálogo não configurado');
    }
  };

  const checkSlugAvailability = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    
    setCheckingSlug(true);
    try {
      const response = await api.get(`/catalogo/check-slug/${slug}`);
      setSlugAvailable(response.data.available);
      if (!response.data.available) {
        toast.warning(response.data.error);
      }
    } catch (error) {
      setSlugAvailable(false);
    } finally {
      setCheckingSlug(false);
    }
  };

  const handleSaveSlug = async () => {
    if (!newSlug || newSlug.length < 3) {
      toast.warning('O nome deve ter no mínimo 3 caracteres');
      return;
    }
    
    setSavingCatalog(true);
    try {
      const response = await api.post('/catalogo/set-slug', { slug: newSlug });
      toast.success('Link do catálogo salvo!');
      setCatalogConfig(prev => ({
        ...prev,
        slug: response.data.slug,
        url: response.data.url,
        enabled: true
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSavingCatalog(false);
    }
  };

  const handleSaveCatalogConfig = async () => {
    setSavingCatalog(true);
    try {
      await api.put('/catalogo/config', {
        catalog_enabled: catalogConfig.enabled,
        whatsapp: catalogConfig.whatsapp,
        phone: catalogConfig.phone,
        address: catalogConfig.address,
        instagram: catalogConfig.instagram,
        description: catalogConfig.description,
        primary_color: catalogConfig.primary_color || null,
        delivery_enabled: catalogConfig.delivery_enabled,
        delivery_fee: parseFloat(catalogConfig.delivery_fee) || 0,
        min_order_value: parseFloat(catalogConfig.min_order_value) || 0
      });
      toast.success('Configurações do catálogo salvas!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingCatalog(false);
    }
  };

  const loadCertificado = async () => {
    try {
      const response = await api.get('/settings/certificado');
      setCertificado(response.data);
    } catch (error) {
      console.log('Certificado não configurado');
    }
  };

  const handleUploadCertificado = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!certificadoSenha) {
      toast.warning('Digite a senha do certificado');
      return;
    }
    
    if (!file.name.endsWith('.pfx') && !file.name.endsWith('.p12')) {
      toast.error('Selecione um arquivo .pfx ou .p12');
      return;
    }
    
    setUploadingCert(true);
    try {
      // Converter arquivo para base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          const base64Data = result.split(',')[1]; // Remove o prefixo "data:..."
          resolve(base64Data);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
      
      // Enviar como JSON
      await api.post('/settings/certificado', {
        certificado_base64: base64,
        certificado_senha: certificadoSenha
      });
      
      toast.success('Certificado enviado com sucesso!');
      setCertificadoSenha('');
      loadCertificado();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar certificado');
    } finally {
      setUploadingCert(false);
    }
  };

  // Converte qualquer erro da API (incl. 422 com detail em array) em texto legível
  const parseApiError = (error) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d) => {
          const campo = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : (d?.loc || 'campo');
          return `${campo}: ${d?.msg || 'inválido'}`;
        })
        .join('; ');
    }
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    return error?.response?.data?.message || error?.message || 'Erro desconhecido';
  };

  const handleSavePdvConfig = () => {
    localStorage.setItem('config_pdv', JSON.stringify(pdvConfig));
    toast.success('Configurações do PDV salvas!');
  };

  const handleSave = async () => {
    setSaving(true);

    localStorage.setItem('config_balanca', JSON.stringify(balancaConfig));
    localStorage.setItem('config_pdv', JSON.stringify(pdvConfig));
    localStorage.setItem('config_empresa', JSON.stringify(empresaConfig));
    localStorage.setItem('config_fiscal', JSON.stringify(fiscalConfig));

    let companyErr = null;
    let fiscalErr = null;

    // Salvar dados da empresa no backend
    try {
      await api.post('/settings/company', {
        razao_social: empresaConfig.razaoSocial,
        nome_fantasia: empresaConfig.nomeFantasia,
        cnpj: empresaConfig.cnpj,
        inscricao_estadual: empresaConfig.inscricaoEstadual,
        ie: empresaConfig.inscricaoEstadual,
        inscricao_municipal: empresaConfig.inscricaoMunicipal,
        endereco: empresaConfig.endereco,
        logradouro: empresaConfig.endereco,
        numero: empresaConfig.numero,
        complemento: empresaConfig.complemento,
        bairro: empresaConfig.bairro,
        cidade: empresaConfig.cidade,
        uf: empresaConfig.uf,
        cep: empresaConfig.cep,
        cod_municipio: empresaConfig.codigoMunicipio,
        telefone: empresaConfig.telefone,
        email: empresaConfig.email
      });
    } catch (error) {
      companyErr = parseApiError(error);
      console.error('Erro ao salvar dados da empresa no servidor:', error);
    }

    // Salvar configurações fiscais no backend
    try {
      await api.post('/settings/fiscal', {
        ambiente_nfce: fiscalConfig.ambienteNfce === '1' ? 'producao' : 'homologacao',
        ambiente_nfe: fiscalConfig.ambienteNfe === '1' ? 'producao' : 'homologacao',
        serie_nfce: String(fiscalConfig.serieNfce ?? '1') || '1',
        serie_nfe: String(fiscalConfig.serieNfe ?? '1') || '1',
        proximo_numero: String(fiscalConfig.proximoNumeroNfce ?? '1') || '1',
        proximo_numero_nfe: String(fiscalConfig.proximoNumeroNfe ?? '1') || '1',
        csc: (fiscalConfig.csc || '').trim(),
        id_csc: (fiscalConfig.idCsc || '').trim(),
        regime_tributario: fiscalConfig.regimeTributario || '1',
        uf: fiscalConfig.uf || '',
        habilitar_nfce: !!fiscalConfig.habilitarNfce,
        habilitar_nfe: !!fiscalConfig.habilitarNfe,
        // Responsável Técnico (NT 2018.005 - opcional)
        resp_tec_cnpj: (fiscalConfig.respTecCnpj || '').replace(/\D/g, ''),
        resp_tec_contato: fiscalConfig.respTecContato || '',
        resp_tec_email: fiscalConfig.respTecEmail || '',
        resp_tec_fone: (fiscalConfig.respTecFone || '').replace(/\D/g, '')
      });
    } catch (error) {
      fiscalErr = parseApiError(error);
      console.error('Erro ao salvar configurações fiscais no servidor:', error);
    }

    setSaving(false);

    // Só declarar sucesso se o save fiscal realmente funcionou — evita o
    // usuário achar que salvou o CSC quando na verdade falhou (e o diagnóstico
    // continua PENDENTE).
    if (fiscalErr) {
      toast.error(`Falha ao salvar configurações fiscais (CSC/NFC-e): ${fiscalErr}. Tente novamente.`);
    } else if (companyErr) {
      toast.warning(`Configurações fiscais salvas, mas houve erro ao salvar a empresa: ${companyErr}`);
    } else {
      toast.success('Configurações salvas com sucesso!');
    }

    // Recarrega do servidor para refletir exatamente o que foi persistido
    try {
      if (typeof loadFiscalConfig === 'function') await loadFiscalConfig();
    } catch (_) { /* silencioso */ }
  };

  const tabs = [
    { id: 'empresa', label: 'Empresa', icon: Building2 },
    { id: 'fiscal', label: 'Fiscal', icon: FileText },
    { id: 'pagamentos', label: 'Pagamentos', icon: CreditCard },
    { id: 'pdv', label: 'PDV', icon: Monitor },
    { id: 'impressora', label: 'Impressora', icon: Printer },
    { id: 'catalogo', label: 'Catálogo Online', icon: Globe },
    { id: 'balanca', label: 'Balança', icon: Scale },
    { id: 'migracao', label: 'Migração', icon: Import },
    { id: 'app', label: 'Instalar App', icon: Download },
  ];

  // Funções de Migração
  const handleImportarCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      toast.error('Selecione um arquivo CSV ou Excel');
      return;
    }
    
    setImportandoCsv(true);
    setCsvFile(file.name);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/products/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${response.data.imported} produtos importados com sucesso!`);
      setMigracaoStatus({
        success: true,
        message: `${response.data.imported} produtos importados`,
        details: response.data
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
      setMigracaoStatus({
        success: false,
        message: error.response?.data?.detail || 'Erro na importação'
      });
    } finally {
      setImportandoCsv(false);
    }
  };

  const handleTestarConexaoFirebird = async () => {
    setMigrando(true);
    setMigracaoStatus(null);
    
    try {
      const response = await api.post('/migration/test-firebird', migracaoConfig);
      setMigracaoStatus({
        success: true,
        message: `Conexão OK! ${response.data.products_count} produtos encontrados`,
        tables: response.data.tables
      });
      toast.success('Conexão com Firebird estabelecida!');
    } catch (error) {
      setMigracaoStatus({
        success: false,
        message: error.response?.data?.detail || 'Erro ao conectar ao Firebird'
      });
      toast.error('Erro ao conectar ao Firebird');
    } finally {
      setMigrando(false);
    }
  };

  const handleMigrarFirebird = async () => {
    if (!window.confirm('Isso irá importar todos os produtos do Firebird. Deseja continuar?')) {
      return;
    }
    
    setMigrando(true);
    setMigracaoStatus(null);
    
    try {
      const response = await api.post('/migration/firebird', migracaoConfig);
      setMigracaoStatus({
        success: true,
        message: `Migração concluída! ${response.data.inserted} produtos inseridos, ${response.data.updated} atualizados`,
        details: response.data
      });
      toast.success(`${response.data.inserted} produtos importados do Firebird!`);
    } catch (error) {
      setMigracaoStatus({
        success: false,
        message: error.response?.data?.detail || 'Erro na migração'
      });
      toast.error('Erro na migração');
    } finally {
      setMigrando(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Settings className="w-8 h-8 text-amber-500" />
            Configurações do Sistema
          </h1>
          <p className="text-slate-600 mt-1">Gerencie as configurações do PDV, balança, empresa e fiscal</p>
        </div>

        <div className="flex gap-6">
          {/* Sidebar Tabs */}
          <div className="w-56 shrink-0">
            <Card>
              <CardContent className="p-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`config-tab-${tab.id}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-amber-500 text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Content */}
          <div className="flex-1">
            {/* Catálogo Online */}
            {activeTab === 'catalogo' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-emerald-500" />
                    Catálogo Online
                  </CardTitle>
                  <CardDescription>Configure seu catálogo de produtos online com pedidos</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Definir Slug */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Link do seu catálogo</Label>
                    <div className="flex gap-2">
                      <div className="flex-1 flex items-center">
                        <span className="px-3 py-2 bg-slate-100 border border-r-0 rounded-l-md text-sm text-slate-500">
                          /catalogo/
                        </span>
                        <Input
                          value={newSlug}
                          onChange={(e) => {
                            const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            setNewSlug(value);
                            setSlugAvailable(null);
                          }}
                          onBlur={() => checkSlugAvailability(newSlug)}
                          placeholder="sua-loja"
                          className="rounded-l-none"
                        />
                      </div>
                      {checkingSlug ? (
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400 mt-2" />
                      ) : slugAvailable === true ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500 mt-2" />
                      ) : slugAvailable === false ? (
                        <XCircle className="w-5 h-5 text-rose-500 mt-2" />
                      ) : null}
                      <Button onClick={handleSaveSlug} disabled={savingCatalog || !newSlug}>
                        {savingCatalog ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-sm text-slate-500">
                      Use apenas letras, números e hífen. Mínimo 3 caracteres.
                    </p>
                    
                    {catalogConfig.url && (
                      <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                        <Link className="w-4 h-4 text-emerald-600" />
                        <span className="text-emerald-700 font-medium">Seu catálogo:</span>
                        <a 
                          href={catalogConfig.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-emerald-600 hover:underline flex items-center gap-1"
                        >
                          {window.location.origin}{catalogConfig.url}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  </div>
                  
                  <Separator />
                  
                  {/* Ativar/Desativar */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Catálogo Ativo</Label>
                      <p className="text-sm text-slate-500">Permite que clientes vejam seus produtos online</p>
                    </div>
                    <Switch
                      checked={catalogConfig.enabled}
                      onCheckedChange={(checked) => setCatalogConfig({...catalogConfig, enabled: checked})}
                    />
                  </div>
                  
                  <Separator />

                  <CatalogMediaUploader config={catalogConfig} setConfig={setCatalogConfig} toast={toast} />

                  <Separator />

                  <div className="space-y-3" data-testid="catalog-color-section">
                    <div>
                      <Label className="text-base font-semibold">Cor principal do catálogo</Label>
                      <p className="text-sm text-slate-500">Usada nos botões, preços e destaques da página do seu catálogo.</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {['#059669', '#dc2626', '#ea580c', '#d97706', '#2563eb', '#7c3aed', '#db2777', '#0f172a'].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setCatalogConfig({ ...catalogConfig, primary_color: c })}
                          className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${(catalogConfig.primary_color || '#059669') === c ? 'border-slate-900 ring-2 ring-offset-2 ring-slate-300' : 'border-white shadow'}`}
                          style={{ backgroundColor: c }}
                          aria-label={`Cor ${c}`}
                          data-testid={`catalog-color-${c.slice(1)}`}
                        />
                      ))}
                      <label className="flex items-center gap-2 text-sm text-slate-600 ml-2">
                        <input
                          type="color"
                          value={catalogConfig.primary_color || '#059669'}
                          onChange={(e) => setCatalogConfig({ ...catalogConfig, primary_color: e.target.value })}
                          className="w-9 h-9 rounded cursor-pointer border-0 bg-transparent"
                          data-testid="catalog-color-picker"
                        />
                        Personalizada
                      </label>
                      <span className="font-mono text-xs text-slate-500" data-testid="catalog-color-value">{catalogConfig.primary_color || '#059669'}</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg border bg-slate-50">
                      <span className="text-xs text-slate-500">Prévia:</span>
                      <span className="font-bold" style={{ color: catalogConfig.primary_color || '#059669' }}>R$ 19,90</span>
                      <span className="px-3 py-1.5 rounded-md text-white text-sm" style={{ backgroundColor: catalogConfig.primary_color || '#059669' }}>Adicionar</span>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  {/* WhatsApp e Contato */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>WhatsApp (para pedidos)</Label>
                      <Input
                        value={catalogConfig.whatsapp}
                        onChange={(e) => setCatalogConfig({...catalogConfig, whatsapp: e.target.value})}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                    <div>
                      <Label>Telefone</Label>
                      <Input
                        value={catalogConfig.phone}
                        onChange={(e) => setCatalogConfig({...catalogConfig, phone: e.target.value})}
                        placeholder="(00) 0000-0000"
                      />
                    </div>
                    <div>
                      <Label>Instagram (sem @)</Label>
                      <Input
                        value={catalogConfig.instagram}
                        onChange={(e) => setCatalogConfig({...catalogConfig, instagram: e.target.value})}
                        placeholder="sualoja"
                      />
                    </div>
                    <div>
                      <Label>Endereço</Label>
                      <Input
                        value={catalogConfig.address}
                        onChange={(e) => setCatalogConfig({...catalogConfig, address: e.target.value})}
                        placeholder="Rua, número - Bairro"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Descrição da loja</Label>
                    <Textarea
                      value={catalogConfig.description}
                      onChange={(e) => setCatalogConfig({...catalogConfig, description: e.target.value})}
                      placeholder="Uma breve descrição da sua loja..."
                      rows={2}
                    />
                  </div>
                  
                  <Separator />
                  
                  {/* Entrega */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Entrega Habilitada</Label>
                        <p className="text-sm text-slate-500">Permite que clientes escolham entrega</p>
                      </div>
                      <Switch
                        checked={catalogConfig.delivery_enabled}
                        onCheckedChange={(checked) => setCatalogConfig({...catalogConfig, delivery_enabled: checked})}
                      />
                    </div>
                    
                    {catalogConfig.delivery_enabled && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Taxa de Entrega (R$)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={catalogConfig.delivery_fee}
                            onChange={(e) => setCatalogConfig({...catalogConfig, delivery_fee: e.target.value})}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label>Pedido Mínimo (R$)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={catalogConfig.min_order_value}
                            onChange={(e) => setCatalogConfig({...catalogConfig, min_order_value: e.target.value})}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <Button onClick={handleSaveCatalogConfig} disabled={savingCatalog}>
                      {savingCatalog ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Salvar Configurações
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* PDV Config */}
            {activeTab === 'pdv' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-amber-500" />
                    Configurações do PDV
                  </CardTitle>
                  <CardDescription>Configure o comportamento do ponto de venda</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Emitir NFC-e Automaticamente</Label>
                        <p className="text-sm text-slate-500">Emite a nota fiscal ao finalizar a venda</p>
                      </div>
                      <Switch
                        checked={pdvConfig.emitirNfceAutomatico}
                        onCheckedChange={(checked) => setPdvConfig({...pdvConfig, emitirNfceAutomatico: checked})}
                      />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Imprimir Cupom</Label>
                        <p className="text-sm text-slate-500">Imprime cupom automaticamente após venda</p>
                      </div>
                      <Switch
                        checked={pdvConfig.imprimirCupom}
                        onCheckedChange={(checked) => setPdvConfig({...pdvConfig, imprimirCupom: checked})}
                      />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Solicitar Cliente</Label>
                        <p className="text-sm text-slate-500">Exige seleção de cliente antes de finalizar</p>
                      </div>
                      <Switch
                        checked={pdvConfig.solicitarCliente}
                        onCheckedChange={(checked) => setPdvConfig({...pdvConfig, solicitarCliente: checked})}
                      />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Mostrar Estoque</Label>
                        <p className="text-sm text-slate-500">Exibe quantidade em estoque no PDV</p>
                      </div>
                      <Switch
                        checked={pdvConfig.mostrarEstoque}
                        onCheckedChange={(checked) => setPdvConfig({...pdvConfig, mostrarEstoque: checked})}
                      />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Permitir Venda Negativa</Label>
                        <p className="text-sm text-slate-500">Permite vender mesmo sem estoque</p>
                      </div>
                      <Switch
                        checked={pdvConfig.permitirVendaNegativa}
                        onCheckedChange={(checked) => setPdvConfig({...pdvConfig, permitirVendaNegativa: checked})}
                        data-testid="pdv-venda-negativa-switch"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={handleSavePdvConfig} data-testid="save-pdv-config-button">
                      <Save className="w-4 h-4 mr-2" />
                      Salvar Configurações
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Migração de Produtos */}
            {activeTab === 'migracao' && (
              <div className="space-y-6">
                {/* Guia Passo a Passo */}
                <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-emerald-800">
                      <FileSpreadsheet className="w-6 h-6" />
                      Importar Produtos via Planilha
                    </CardTitle>
                    <CardDescription className="text-emerald-700">
                      A maneira mais fácil de trazer seus produtos de outro sistema
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Passo a Passo */}
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white rounded-lg p-4 border border-emerald-200">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">1</div>
                          <span className="font-semibold text-slate-700">Exporte do seu sistema</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          No seu sistema atual, exporte os produtos para Excel ou CSV
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-emerald-200">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">2</div>
                          <span className="font-semibold text-slate-700">Ajuste as colunas</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Verifique se tem: nome, preço, categoria (mínimo)
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-emerald-200">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">3</div>
                          <span className="font-semibold text-slate-700">Faça o upload</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Arraste o arquivo ou clique para selecionar
                        </p>
                      </div>
                    </div>

                    {/* Área de Upload */}
                    <div className="mb-4">
                      <label className="flex items-center justify-center w-full h-40 px-4 transition bg-white border-2 border-emerald-300 border-dashed rounded-xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50">
                        <div className="flex flex-col items-center space-y-3">
                          {importandoCsv ? (
                            <>
                              <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
                              <span className="text-emerald-600 font-medium">Importando produtos...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-12 h-12 text-emerald-400" />
                              <div className="text-center">
                                <span className="text-lg font-medium text-slate-700">
                                  {csvFile || 'Clique aqui ou arraste seu arquivo'}
                                </span>
                                <p className="text-sm text-slate-500 mt-1">Aceita: .csv, .xlsx, .xls</p>
                              </div>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          className="hidden"
                          onChange={handleImportarCSV}
                          disabled={importandoCsv}
                        />
                      </label>
                    </div>

                    {/* Status da Importação */}
                    {migracaoStatus && (
                      <div className={`p-4 rounded-lg mb-4 ${migracaoStatus.success ? 'bg-emerald-100 border border-emerald-300' : 'bg-rose-100 border border-rose-300'}`}>
                        <div className="flex items-center gap-2">
                          {migracaoStatus.success ? (
                            <CheckCircle className="w-6 h-6 text-emerald-600" />
                          ) : (
                            <XCircle className="w-6 h-6 text-rose-600" />
                          )}
                          <span className={`font-medium ${migracaoStatus.success ? 'text-emerald-800' : 'text-rose-800'}`}>
                            {migracaoStatus.message}
                          </span>
                        </div>
                        {migracaoStatus.details?.error_details?.length > 0 && (
                          <div className="mt-2 text-sm text-rose-700">
                            <p className="font-medium">Erros encontrados:</p>
                            <ul className="list-disc list-inside">
                              {migracaoStatus.details.error_details.slice(0, 5).map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Botão Modelo */}
                    <Button 
                      variant="outline" 
                      className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => {
                        const csvContent = "nome,preco,categoria,estoque,codigo_barras,custo\nCoca-Cola 2L,12.00,Refrigerantes,50,7891234567890,7.20\nCerveja Brahma 350ml,4.50,Cervejas,100,7891234567891,2.70\nPão Francês,15.00,Padaria,30,,9.00";
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'modelo_produtos_nfcesyspdv.csv';
                        a.click();
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Baixar Planilha Modelo (CSV)
                    </Button>
                  </CardContent>
                </Card>

                {/* Colunas Aceitas */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Colunas Aceitas na Planilha</CardTitle>
                    <CardDescription>O sistema reconhece automaticamente estas colunas</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm"><strong>Nome:</strong> nome, name, produto, descricao</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm"><strong>Preço:</strong> preco, price, valor, preco_venda</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm"><strong>Categoria:</strong> categoria, category, grupo, secao</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm"><strong>Estoque:</strong> estoque, stock, quantidade</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm"><strong>Código:</strong> codigo_barras, barcode, ean, codigo</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm"><strong>Custo:</strong> custo, cost, preco_custo</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm"><strong>NCM:</strong> ncm</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm"><strong>Unidade:</strong> unidade, un</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>Dica:</strong> Campos obrigatórios são apenas <strong>nome</strong>, <strong>preço</strong> e <strong>categoria</strong>. 
                        Os demais são opcionais.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Ajuda para Sistemas Específicos */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Como Exportar do Seu Sistema</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <details className="border rounded-lg">
                        <summary className="px-4 py-3 cursor-pointer font-medium text-slate-700 hover:bg-slate-50">
                          SigLight / Sig2000
                        </summary>
                        <div className="px-4 py-3 border-t bg-slate-50 text-sm text-slate-600">
                          <ol className="list-decimal list-inside space-y-1">
                            <li>Acesse o menu <strong>Cadastros → Produtos</strong></li>
                            <li>Clique em <strong>Exportar</strong> ou <strong>Imprimir</strong></li>
                            <li>Escolha o formato <strong>Excel</strong> ou <strong>CSV</strong></li>
                            <li>Salve o arquivo e faça upload aqui</li>
                          </ol>
                        </div>
                      </details>
                      <details className="border rounded-lg">
                        <summary className="px-4 py-3 cursor-pointer font-medium text-slate-700 hover:bg-slate-50">
                          Excel / Planilha Google
                        </summary>
                        <div className="px-4 py-3 border-t bg-slate-50 text-sm text-slate-600">
                          <ol className="list-decimal list-inside space-y-1">
                            <li>Organize seus produtos em colunas: nome, preco, categoria</li>
                            <li>A primeira linha deve ter os nomes das colunas</li>
                            <li>Salve como <strong>CSV (separado por vírgulas)</strong></li>
                            <li>Faça upload do arquivo</li>
                          </ol>
                        </div>
                      </details>
                      <details className="border rounded-lg">
                        <summary className="px-4 py-3 cursor-pointer font-medium text-slate-700 hover:bg-slate-50">
                          Outros Sistemas
                        </summary>
                        <div className="px-4 py-3 border-t bg-slate-50 text-sm text-slate-600">
                          <p>A maioria dos sistemas possui opção de exportar para Excel ou CSV.</p>
                          <p className="mt-2">Procure por: <strong>Exportar</strong>, <strong>Relatórios</strong>, <strong>Backup</strong> ou <strong>Integração</strong></p>
                          <p className="mt-2">Se precisar de ajuda, entre em contato: <strong>11 94728-9661</strong></p>
                        </div>
                      </details>
                    </div>
                  </CardContent>
                </Card>

                {/* Migração Avançada - Firebird */}
                <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-blue-800">
                      <Database className="w-6 h-6" />
                      Migração Avançada - Banco Firebird
                    </CardTitle>
                    <CardDescription className="text-blue-700">
                      Importe dados diretamente de sistemas que usam Firebird (.gdb / .fdb)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-white rounded-lg p-4 border border-blue-200 mb-4">
                      <h4 className="font-semibold text-slate-700 mb-2">Sistemas Compatíveis:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-slate-600">
                        <span>• SigLight / Sig2000</span>
                        <span>• Retaguarda PBM</span>
                        <span>• Visual Mix</span>
                        <span>• Outros Firebird</span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label>Senha do Banco (padrão: masterkey)</Label>
                        <Input
                          type="password"
                          value={migracaoConfig.password || 'masterkey'}
                          onChange={(e) => setMigracaoConfig({...migracaoConfig, password: e.target.value})}
                          placeholder="Senha do banco Firebird"
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                        <input
                          type="file"
                          accept=".gdb,.fdb"
                          className="hidden"
                          id="firebird-upload"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            setMigrando(true);
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('password', migracaoConfig.password || 'masterkey');
                            
                            try {
                              const response = await api.post('/migration/upload-database', formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              if (response.data.success) {
                                toast.success(response.data.message);
                                setMigracaoStatus({
                                  success: true,
                                  message: `Banco carregado! ${response.data.database_info?.tables_count || 0} tabelas encontradas.`,
                                  details: response.data.database_info
                                });
                              } else {
                                toast.error(response.data.message);
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.message || 'Erro ao fazer upload do banco');
                            } finally {
                              setMigrando(false);
                            }
                          }}
                          disabled={migrando}
                        />
                        <label htmlFor="firebird-upload" className="cursor-pointer">
                          {migrando ? (
                            <div className="flex flex-col items-center gap-2">
                              <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                              <span className="text-blue-600 font-medium">Carregando banco de dados...</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-2">
                              <Server className="w-12 h-12 text-blue-400" />
                              <span className="text-lg font-medium text-slate-700">
                                Clique para selecionar arquivo .gdb ou .fdb
                              </span>
                              <span className="text-sm text-slate-500">
                                Arquivo de banco de dados Firebird
                              </span>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                          <div className="text-sm text-amber-800">
                            <p className="font-semibold">Como encontrar o arquivo .gdb:</p>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              <li>Geralmente está em C:\Sistemas ou C:\Dados</li>
                              <li>Pode estar na pasta do programa instalado</li>
                              <li>Faça backup do arquivo antes de migrar</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Balança Config */}
            {activeTab === 'balanca' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="w-5 h-5 text-amber-500" />
                    Configurações da Balança
                  </CardTitle>
                  <CardDescription>Configure a integração com balanças comerciais para pesagem automática</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Habilitar Balança */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-base font-semibold">Habilitar Balança</Label>
                      <p className="text-sm text-slate-500">Ativa a conexão automática com a balança no PDV</p>
                    </div>
                    <Switch
                      checked={balancaConfig.habilitada}
                      onCheckedChange={(checked) => setBalancaConfig({...balancaConfig, habilitada: checked})}
                    />
                  </div>

                  {balancaConfig.habilitada && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Modelo da Balança</Label>
                          <select
                            value={balancaConfig.tipo}
                            onChange={(e) => setBalancaConfig({...balancaConfig, tipo: e.target.value})}
                            className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                          >
                            <option value="generica">Genérica (Auto-detectar)</option>
                            <option value="toledo_prix">Toledo Prix</option>
                            <option value="filizola">Filizola</option>
                            <option value="urano">Urano</option>
                            <option value="balmak">Balmak</option>
                            <option value="elgin">Elgin</option>
                          </select>
                        </div>
                        
                        <div>
                          <Label>Velocidade (Baud Rate)</Label>
                          <select
                            value={balancaConfig.baud_rate}
                            onChange={(e) => setBalancaConfig({...balancaConfig, baud_rate: parseInt(e.target.value)})}
                            className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                          >
                            <option value="4800">4800</option>
                            <option value="9600">9600</option>
                            <option value="19200">19200</option>
                            <option value="38400">38400</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <Label className="font-medium">Conectar Automaticamente</Label>
                          <p className="text-xs text-slate-500">Reconecta ao abrir o PDV se a balança estiver disponível</p>
                        </div>
                        <Switch
                          checked={balancaConfig.auto_conectar}
                          onCheckedChange={(checked) => setBalancaConfig({...balancaConfig, auto_conectar: checked})}
                        />
                      </div>

                      {/* Botão de Teste */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="font-semibold text-blue-800 mb-2">Autorizar Porta Serial</p>
                        <p className="text-sm text-blue-700 mb-3">
                          Clique para selecionar a porta USB da balança. Isso autoriza o navegador a reconectar automaticamente.
                        </p>
                        <Button
                          onClick={testBalancaConnection}
                          disabled={testingBalanca}
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        >
                          {testingBalanca ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Conectando...
                            </>
                          ) : (
                            <>
                              <Link className="w-4 h-4 mr-2" />
                              Testar Conexão
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                  
                  <Separator />
                  
                  <div>
                    <Label className="text-base font-semibold">Código de Barras da Balança</Label>
                    <p className="text-sm text-slate-500 mb-4">Configure como o código de barras gerado pela balança é interpretado</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Prefixo de Produto Pesado</Label>
                        <Input
                          value={balancaConfig.prefixoPesado}
                          onChange={(e) => setBalancaConfig({...balancaConfig, prefixoPesado: e.target.value})}
                          placeholder="2"
                          maxLength={2}
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">Geralmente "2" ou "02"</p>
                      </div>
                      
                      <div>
                        <Label>Formato do Código</Label>
                        <select
                          value={balancaConfig.formatoCodigo}
                          onChange={(e) => setBalancaConfig({...balancaConfig, formatoCodigo: e.target.value})}
                          className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                        >
                          <option value="padrao">Padrão (2CCCCPPPPPD)</option>
                          <option value="toledo">Toledo (2CCCCCPPPPD)</option>
                          <option value="filizola">Filizola (02CCCCPPPPP)</option>
                        </select>
                      </div>
                      
                      <div>
                        <Label>Casas Decimais do Peso</Label>
                        <select
                          value={balancaConfig.casasDecimais}
                          onChange={(e) => setBalancaConfig({...balancaConfig, casasDecimais: parseInt(e.target.value)})}
                          className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                        >
                          <option value="2">2 casas (0.00)</option>
                          <option value="3">3 casas (0.000)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="font-semibold text-amber-800">Exemplo de código de barras:</p>
                    <p className="text-sm text-amber-700 mt-1">
                      <code className="bg-amber-100 px-2 py-1 rounded">2012300500006</code>
                    </p>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1">
                      <li>• <strong>2</strong> = Prefixo produto pesado</li>
                      <li>• <strong>0123</strong> = Código do produto</li>
                      <li>• <strong>00500</strong> = Peso (0.500 kg)</li>
                      <li>• <strong>6</strong> = Dígito verificador</li>
                    </ul>
                  </div>

                  {/* Botão Salvar */}
                  <div className="flex justify-end">
                    <Button onClick={saveBalancaConfig} disabled={savingBalanca}>
                      {savingBalanca ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Salvar Configurações
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empresa Config */}
            {activeTab === 'empresa' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-amber-500" />
                    Dados da Empresa
                  </CardTitle>
                  <CardDescription>Informações cadastrais da empresa para emissão de NFC-e e NF-e</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* CNPJ com botão de busca */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Label className="text-blue-800 font-semibold">Buscar dados pelo CNPJ</Label>
                    <p className="text-sm text-blue-600 mb-3">Digite o CNPJ e clique em buscar para preencher automaticamente os dados da empresa</p>
                    <div className="flex gap-2">
                      <Input
                        value={empresaConfig.cnpj}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, cnpj: e.target.value})}
                        placeholder="00.000.000/0000-00"
                        className="flex-1 bg-white"
                      />
                      <Button 
                        onClick={() => buscarCnpj(empresaConfig.cnpj)}
                        disabled={buscandoCnpj || !empresaConfig.cnpj}
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        {buscandoCnpj ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Buscando...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Buscar CNPJ
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Razão Social *</Label>
                      <Input
                        value={empresaConfig.razaoSocial}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, razaoSocial: e.target.value})}
                        placeholder="Razão Social da Empresa LTDA"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Nome Fantasia</Label>
                      <Input
                        value={empresaConfig.nomeFantasia}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, nomeFantasia: e.target.value})}
                        placeholder="Nome Fantasia"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>CNPJ *</Label>
                      <Input
                        value={empresaConfig.cnpj}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, cnpj: e.target.value})}
                        placeholder="00.000.000/0000-00"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Inscrição Estadual *</Label>
                      <Input
                        value={empresaConfig.inscricaoEstadual}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, inscricaoEstadual: e.target.value})}
                        placeholder="000.000.000.000"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Inscrição Municipal</Label>
                      <Input
                        value={empresaConfig.inscricaoMunicipal}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, inscricaoMunicipal: e.target.value})}
                        placeholder="Opcional"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Telefone</Label>
                      <Input
                        value={empresaConfig.telefone}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, telefone: e.target.value})}
                        placeholder="(00) 00000-0000"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>E-mail</Label>
                      <Input
                        type="email"
                        value={empresaConfig.email}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, email: e.target.value})}
                        placeholder="empresa@email.com"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <h3 className="text-lg font-semibold text-slate-800">Endereço</h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label>Logradouro *</Label>
                      <Input
                        value={empresaConfig.endereco}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, endereco: e.target.value})}
                        placeholder="Rua, Avenida..."
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Número *</Label>
                      <Input
                        value={empresaConfig.numero}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, numero: e.target.value})}
                        placeholder="123"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Complemento</Label>
                      <Input
                        value={empresaConfig.complemento}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, complemento: e.target.value})}
                        placeholder="Sala, Andar, etc."
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Bairro *</Label>
                      <Input
                        value={empresaConfig.bairro}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, bairro: e.target.value})}
                        placeholder="Bairro"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>CEP *</Label>
                      <Input
                        value={empresaConfig.cep}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, cep: e.target.value})}
                        placeholder="00000-000"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Cidade *</Label>
                      <Input
                        value={empresaConfig.cidade}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, cidade: e.target.value})}
                        placeholder="Cidade"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>UF *</Label>
                      <select
                        value={empresaConfig.uf}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, uf: e.target.value})}
                        className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                      >
                        {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label>Código Município (IBGE) *</Label>
                      <Input
                        value={empresaConfig.codigoMunicipio}
                        onChange={(e) => setEmpresaConfig({...empresaConfig, codigoMunicipio: e.target.value})}
                        placeholder="3550308"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">Código IBGE de 7 dígitos - obrigatório para NF-e</p>
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Importante:</strong> Estes dados são utilizados para emissão de NFC-e (modelo 65) e NF-e (modelo 55). 
                      Certifique-se de que todas as informações estão corretas e atualizadas.
                    </p>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button onClick={saveEmpresaConfig} disabled={savingEmpresa} className="bg-amber-500 hover:bg-amber-600">
                      {savingEmpresa ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Salvar Dados da Empresa
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pagamentos Config */}
            {activeTab === 'pagamentos' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-amber-500" />
                    Formas de Pagamento e Gateways
                  </CardTitle>
                  <CardDescription>Cadastre as formas de pagamento do PDV e configure seus gateways (PIX, cartão)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <PaymentMethodsSettings />

                  {/* Mercado Pago */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#009EE3] rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold text-sm">MP</span>
                        </div>
                        <div>
                          <h3 className="font-semibold">Mercado Pago</h3>
                          <p className="text-sm text-slate-500">PIX, Cartão de Crédito e Débito</p>
                        </div>
                      </div>
                      <Switch
                        checked={paymentConfig.mercadopago_habilitado}
                        onCheckedChange={(checked) => setPaymentConfig({...paymentConfig, mercadopago_habilitado: checked})}
                        data-testid="mp-switch"
                      />
                    </div>
                    
                    {paymentConfig.mercadopago_habilitado && (
                      <div className="space-y-3 pt-3 border-t">
                        <div>
                          <Label>Access Token *</Label>
                          <div className="relative">
                            <Input
                              type={showMpToken ? "text" : "password"}
                              value={paymentConfig.mercadopago_access_token}
                              onChange={(e) => setPaymentConfig({...paymentConfig, mercadopago_access_token: e.target.value})}
                              placeholder="APP_USR-0000000000000000-000000-..."
                              data-testid="mp-token-input"
                            />
                            <button
                              type="button"
                              onClick={() => setShowMpToken(!showMpToken)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                              {showMpToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Obtenha em: <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Mercado Pago Developers</a>
                          </p>
                        </div>
                        
                        <div>
                          <Label>Public Key</Label>
                          <Input
                            value={paymentConfig.mercadopago_public_key}
                            onChange={(e) => setPaymentConfig({...paymentConfig, mercadopago_public_key: e.target.value})}
                            placeholder="APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            data-testid="mp-public-key-input"
                          />
                          <p className="text-xs text-slate-500 mt-1">Usado para checkout no frontend</p>
                        </div>
                        
                        {paymentConfig.mercadopago_access_token && (
                          <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                            <p className="text-sm text-emerald-700 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Token configurado
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* PagSeguro */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FFC72C] rounded-lg flex items-center justify-center">
                          <span className="text-slate-800 font-bold text-sm">PS</span>
                        </div>
                        <div>
                          <h3 className="font-semibold">PagSeguro</h3>
                          <p className="text-sm text-slate-500">PIX, Cartão e Boleto</p>
                        </div>
                      </div>
                      <Switch
                        checked={paymentConfig.pagseguro_habilitado}
                        onCheckedChange={(checked) => setPaymentConfig({...paymentConfig, pagseguro_habilitado: checked})}
                        data-testid="pagseguro-switch"
                      />
                    </div>
                    
                    {paymentConfig.pagseguro_habilitado && (
                      <div className="space-y-3 pt-3 border-t">
                        <div>
                          <Label>Token de Acesso *</Label>
                          <Input
                            type="password"
                            value={paymentConfig.pagseguro_token}
                            onChange={(e) => setPaymentConfig({...paymentConfig, pagseguro_token: e.target.value})}
                            placeholder="Seu token PagSeguro"
                            data-testid="pagseguro-token-input"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Configurações Gerais de PIX */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold">Configurações de PIX</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Tempo de expiração (minutos)</Label>
                        <Input
                          type="number"
                          value={paymentConfig.pix_timeout_minutos}
                          onChange={(e) => setPaymentConfig({...paymentConfig, pix_timeout_minutos: parseInt(e.target.value) || 30})}
                          min="5"
                          max="60"
                          data-testid="pix-timeout-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">Tempo para o cliente pagar o PIX</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Importante:</strong> Os tokens são sensíveis. Nunca compartilhe suas credenciais. 
                      Use tokens de produção apenas quando estiver pronto para receber pagamentos reais.
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={savePaymentConfig} disabled={savingPayment} className="bg-amber-500 hover:bg-amber-600">
                      {savingPayment ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Salvar Configurações de Pagamento
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Fiscal Config */}
            {activeTab === 'fiscal' && (
              <div className="space-y-6">
                {/* Tabs internas da seção fiscal */}
                <div className="flex gap-2 border-b pb-4 flex-wrap">
                  {[
                    { id: 'geral', label: 'Geral', icon: Settings },
                    { id: 'certificado', label: 'Certificado Digital', icon: Key },
                    { id: 'nfce', label: 'NFC-e', icon: Receipt },
                    { id: 'nfe', label: 'NF-e', icon: FileText },
                    { id: 'danfe', label: 'Impressão/DANFE', icon: Printer },
                    { id: 'sefaz', label: 'Servidor SEFAZ', icon: Server },
                    { id: 'diagnostico', label: 'Diagnóstico NFC-e', icon: AlertTriangle },
                    { id: 'diagnostico_nfe', label: 'Diagnóstico NF-e', icon: FileText }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setFiscalTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                        fiscalTab === tab.id 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
                
                {/* TAB: Configurações Gerais */}
                {fiscalTab === 'geral' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5 text-amber-500" />
                        Configurações Gerais
                      </CardTitle>
                      <CardDescription>Regime tributário, ambiente e documentos habilitados</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Regime e Ambiente */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Regime Tributário</Label>
                          <select
                            value={fiscalConfig.regimeTributario}
                            onChange={(e) => setFiscalConfig({...fiscalConfig, regimeTributario: e.target.value})}
                            className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                          >
                            <option value="1">1 - Simples Nacional</option>
                            <option value="2">2 - Simples Nacional (Excesso)</option>
                            <option value="3">3 - Regime Normal (Lucro Presumido/Real)</option>
                          </select>
                        </div>
                        
                        <div>
                          <Label>UF da Empresa</Label>
                          <select
                            value={fiscalConfig.uf}
                            onChange={(e) => setFiscalConfig({...fiscalConfig, uf: e.target.value})}
                            className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                          >
                            {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                              <option key={uf} value={uf}>{uf}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <Separator />
                      
                      {/* Documentos Habilitados */}
                      <div>
                        <Label className="text-base font-semibold">Documentos Fiscais Habilitados</Label>
                        <p className="text-sm text-slate-500 mb-4">Selecione quais documentos fiscais a empresa pode emitir</p>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div 
                            onClick={() => setFiscalConfig({...fiscalConfig, habilitarNfce: !fiscalConfig.habilitarNfce})}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              fiscalConfig.habilitarNfce 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Receipt className="w-6 h-6 text-green-600" />
                                <div>
                                  <p className="font-semibold">NFC-e (Modelo 65)</p>
                                  <p className="text-xs text-slate-500">Nota Fiscal do Consumidor Eletrônica</p>
                                </div>
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                fiscalConfig.habilitarNfce ? 'border-green-500 bg-green-500' : 'border-slate-300'
                              }`}>
                                {fiscalConfig.habilitarNfce && <Check className="w-3 h-3 text-white" />}
                              </div>
                            </div>
                          </div>
                          
                          <div 
                            onClick={() => setFiscalConfig({...fiscalConfig, habilitarNfe: !fiscalConfig.habilitarNfe})}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              fiscalConfig.habilitarNfe 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <FileText className="w-6 h-6 text-blue-600" />
                                <div>
                                  <p className="font-semibold">NF-e (Modelo 55)</p>
                                  <p className="text-xs text-slate-500">Nota Fiscal Eletrônica</p>
                                </div>
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                fiscalConfig.habilitarNfe ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                              }`}>
                                {fiscalConfig.habilitarNfe && <Check className="w-3 h-3 text-white" />}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* TAB: Certificado Digital */}
                {fiscalTab === 'certificado' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Key className="w-5 h-5 text-amber-500" />
                        Certificado Digital A1
                      </CardTitle>
                      <CardDescription>Configure o certificado digital para emissão de documentos fiscais</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Status do Certificado */}
                      {certificado ? (
                        <div className={`rounded-lg p-4 ${
                          fiscalConfig.certificadoStatus === 'válido' ? 'bg-emerald-50 border border-emerald-200' :
                          fiscalConfig.certificadoStatus === 'expirando' ? 'bg-amber-50 border border-amber-200' :
                          'bg-red-50 border border-red-200'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              fiscalConfig.certificadoStatus === 'válido' ? 'bg-emerald-100' :
                              fiscalConfig.certificadoStatus === 'expirando' ? 'bg-amber-100' :
                              'bg-red-100'
                            }`}>
                              <Shield className={`w-6 h-6 ${
                                fiscalConfig.certificadoStatus === 'válido' ? 'text-emerald-600' :
                                fiscalConfig.certificadoStatus === 'expirando' ? 'text-amber-600' :
                                'text-red-600'
                              }`} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold">
                                {certificado.razao_social || certificado.cn}
                              </h4>
                              <p className="text-sm">
                                CNPJ: {certificado.cnpj || 'N/A'}
                              </p>
                              <p className="text-xs mt-1">
                                Válido até: {certificado.validade ? new Date(certificado.validade).toLocaleDateString('pt-BR') : 'N/A'}
                              </p>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => setCertificado(null)}>
                              <Trash2 className="w-4 h-4 mr-1" /> Remover
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                          <Key className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                          <h4 className="font-semibold text-slate-700">Nenhum Certificado Configurado</h4>
                          <p className="text-sm text-slate-500 mt-2 mb-4">
                            Faça upload do certificado digital A1 (.pfx) para emitir notas fiscais
                          </p>
                        </div>
                      )}
                      
                      <Separator />
                      
                      {/* Atualizar Senha do Certificado Existente */}
                      {certificado && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <h4 className="font-semibold text-amber-800 mb-2">Atualizar Senha do Certificado</h4>
                          <p className="text-sm text-amber-600 mb-3">
                            Se você já fez upload do certificado mas a senha não foi salva corretamente, atualize aqui:
                          </p>
                          <div className="flex gap-2">
                            <Input
                              type="password"
                              value={certificadoSenha}
                              onChange={(e) => setCertificadoSenha(e.target.value)}
                              placeholder="Digite a senha do certificado"
                              className="flex-1"
                            />
                            <Button
                              onClick={async () => {
                                if (!certificadoSenha) {
                                  toast.warning('Digite a senha');
                                  return;
                                }
                                try {
                                  await api.post('/settings/certificado/senha', { senha: certificadoSenha });
                                  toast.success('Senha atualizada com sucesso!');
                                  setCertificadoSenha('');
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Erro ao atualizar senha');
                                }
                              }}
                              disabled={!certificadoSenha}
                              className="bg-amber-500 hover:bg-amber-600"
                            >
                              Atualizar Senha
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      <Separator />
                      
                      {/* Upload de Certificado */}
                      <div>
                        <Label className="text-base font-semibold">Enviar Novo Certificado</Label>
                        <p className="text-sm text-slate-500 mb-4">Selecione o arquivo .pfx e informe a senha</p>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Arquivo do Certificado (.pfx)</Label>
                            <Input
                              type="file"
                              accept=".pfx,.p12"
                              onChange={(e) => setCertFile(e.target.files?.[0] || null)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Senha do Certificado</Label>
                            <Input
                              type="password"
                              value={certPassword}
                              onChange={(e) => setCertPassword(e.target.value)}
                              placeholder="Digite a senha do certificado"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        
                        <Button 
                          className="mt-4 bg-amber-500 hover:bg-amber-600"
                          disabled={!certFile || !certPassword || uploadingCert}
                          onClick={async () => {
                            if (!certFile || !certPassword) return;
                            setUploadingCert(true);
                            try {
                              // Converter arquivo para base64
                              const base64 = await new Promise((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = () => {
                                  const result = reader.result;
                                  const base64Data = result.split(',')[1];
                                  resolve(base64Data);
                                };
                                reader.onerror = (error) => reject(error);
                                reader.readAsDataURL(certFile);
                              });
                              
                              // Enviar como JSON
                              await api.post('/settings/certificado', {
                                certificado_base64: base64,
                                certificado_senha: certPassword
                              });
                              
                              toast.success('Certificado enviado com sucesso!');
                              setCertPassword('');
                              setCertFile(null);
                              loadCertificado();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Erro ao enviar certificado');
                            } finally {
                              setUploadingCert(false);
                            }
                          }}
                        >
                          {uploadingCert ? 'Enviando...' : 'Enviar Certificado'}
                        </Button>
                      </div>
                      
                      {/* Informações sobre certificado */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-2">Sobre o Certificado Digital</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• O certificado A1 é um arquivo digital com extensão .pfx ou .p12</li>
                          <li>• Tem validade de 1 ano e pode ser adquirido em autoridades certificadoras</li>
                          <li>• É obrigatório para emissão de NFC-e e NF-e</li>
                          <li>• Autoridades certificadoras: Serasa, Certisign, Safeweb, etc.</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* TAB: NFC-e */}
                {fiscalTab === 'nfce' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-green-500" />
                        Configurações NFC-e
                      </CardTitle>
                      <CardDescription>Nota Fiscal de Consumidor Eletrônica (Modelo 65)</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {!fiscalConfig.habilitarNfce && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="text-amber-800">
                            NFC-e está desabilitada. Habilite na aba "Geral" para configurar.
                          </p>
                        </div>
                      )}
                      
                      {fiscalConfig.habilitarNfce && (
                        <>
                          <div className="grid grid-cols-4 gap-4">
                            <div>
                              <Label>Ambiente</Label>
                              <select
                                value={fiscalConfig.ambienteNfce}
                                onChange={(e) => setFiscalConfig({...fiscalConfig, ambienteNfce: e.target.value})}
                                className={`w-full h-10 px-3 border rounded-lg mt-1 ${
                                  fiscalConfig.ambienteNfce === '1' 
                                    ? 'border-green-500 bg-green-50 text-green-700' 
                                    : 'border-amber-500 bg-amber-50 text-amber-700'
                                }`}
                              >
                                <option value="2">Homologação</option>
                                <option value="1">Produção</option>
                              </select>
                            </div>
                            
                            <div>
                              <Label>Modelo</Label>
                              <Input
                                value="65"
                                disabled
                                className="mt-1 bg-slate-100"
                              />
                            </div>
                            
                            <div>
                              <Label>Série</Label>
                              <Input
                                value={fiscalConfig.serieNfce}
                                onChange={(e) => setFiscalConfig({...fiscalConfig, serieNfce: e.target.value})}
                                placeholder="1"
                                className="mt-1"
                              />
                            </div>
                            
                            <div>
                              <Label>Próximo Número</Label>
                              <Input
                                type="number"
                                value={fiscalConfig.proximoNumeroNfce}
                                onChange={(e) => setFiscalConfig({...fiscalConfig, proximoNumeroNfce: e.target.value})}
                                placeholder="1"
                                className="mt-1"
                              />
                            </div>
                          </div>
                          
                          <Separator />
                          
                          <div>
                            <Label className="text-base font-semibold">CSC - Código de Segurança do Contribuinte</Label>
                            <p className="text-sm text-slate-500 mb-4">Obrigatório para geração do QR Code da NFC-e</p>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>ID do CSC</Label>
                                <Input
                                  value={fiscalConfig.idCsc}
                                  onChange={(e) => setFiscalConfig({...fiscalConfig, idCsc: e.target.value})}
                                  placeholder="000001"
                                  className="mt-1"
                                />
                              </div>
                              
                              <div>
                                <Label>CSC (Token)</Label>
                                <Input
                                  type="password"
                                  value={fiscalConfig.csc}
                                  onChange={(e) => setFiscalConfig({...fiscalConfig, csc: e.target.value})}
                                  placeholder="Token fornecido pela SEFAZ"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                              <p className="text-sm text-green-700">
                                <strong>Como obter o CSC:</strong> Acesse o portal da SEFAZ do seu estado e solicite o CSC na área de contribuinte.
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* TAB: NF-e */}
                {fiscalTab === 'nfe' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-500" />
                        Configurações NF-e
                      </CardTitle>
                      <CardDescription>Nota Fiscal Eletrônica (Modelo 55) - Vendas B2B</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {!fiscalConfig.habilitarNfe && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="text-amber-800">
                            NF-e está desabilitada. Habilite na aba "Geral" para configurar.
                          </p>
                        </div>
                      )}
                      
                      {fiscalConfig.habilitarNfe && (
                        <>
                          {/* Aviso importante sobre configurações compartilhadas */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                              <div>
                                <p className="font-semibold text-blue-800">Configuração Simplificada</p>
                                <p className="text-sm text-blue-700 mt-1">
                                  A NF-e utiliza <strong>automaticamente</strong> as mesmas configurações da NFC-e:
                                </p>
                                <ul className="text-sm text-blue-600 mt-2 space-y-1 list-disc list-inside">
                                  <li>Dados da empresa (Configurações &gt; Empresa)</li>
                                  <li>Certificado digital A1 (Configurações &gt; Fiscal &gt; Certificado)</li>
                                  <li>Regime tributário</li>
                                </ul>
                                <p className="text-sm text-blue-700 mt-2">
                                  Você só precisa configurar os campos abaixo, específicos para NF-e.
                                </p>
                              </div>
                            </div>
                          </div>

                          <Separator />
                          
                          {/* Campos específicos da NF-e */}
                          <div>
                            <Label className="text-base font-semibold">Configurações Específicas NF-e</Label>
                            <p className="text-sm text-slate-500 mb-4">Defina ambiente, série e numeração para NF-e</p>
                            
                            <div className="grid grid-cols-4 gap-4">
                              <div>
                                <Label>Ambiente</Label>
                                <select
                                  value={fiscalConfig.ambienteNfe}
                                  onChange={(e) => setFiscalConfig({...fiscalConfig, ambienteNfe: e.target.value})}
                                  className={`w-full h-10 px-3 border rounded-lg mt-1 ${
                                    fiscalConfig.ambienteNfe === '1' 
                                      ? 'border-green-500 bg-green-50 text-green-700' 
                                      : 'border-amber-500 bg-amber-50 text-amber-700'
                                  }`}
                                >
                                  <option value="2">Homologação (Testes)</option>
                                  <option value="1">Produção (Real)</option>
                                </select>
                                <p className="text-xs text-slate-500 mt-1">
                                  {fiscalConfig.ambienteNfe === '1' 
                                    ? 'Notas com valor fiscal real' 
                                    : 'Notas de teste sem valor fiscal'}
                                </p>
                              </div>
                              
                              <div>
                                <Label>Modelo</Label>
                                <Input
                                  value="55"
                                  disabled
                                  className="mt-1 bg-slate-100"
                                />
                                <p className="text-xs text-slate-500 mt-1">Fixo para NF-e</p>
                              </div>
                              
                              <div>
                                <Label>Série</Label>
                                <Input
                                  value={fiscalConfig.serieNfe}
                                  onChange={(e) => setFiscalConfig({...fiscalConfig, serieNfe: e.target.value})}
                                  placeholder="1"
                                  className="mt-1"
                                />
                                <p className="text-xs text-slate-500 mt-1">Geralmente 1</p>
                              </div>
                              
                              <div>
                                <Label>Próximo Número</Label>
                                <Input
                                  type="number"
                                  value={fiscalConfig.proximoNumeroNfe}
                                  onChange={(e) => setFiscalConfig({...fiscalConfig, proximoNumeroNfe: e.target.value})}
                                  placeholder="1"
                                  className="mt-1"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                  {fiscalConfig.proximoNumeroNfe > 1 
                                    ? `Última emitida: ${parseInt(fiscalConfig.proximoNumeroNfe) - 1}`
                                    : 'Começando do início'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Responsável Técnico (NT 2018.005 - Opcional) */}
                          <Separator />
                          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <p className="font-semibold text-slate-700 mb-2">
                              Responsável Técnico <span className="text-xs font-normal text-slate-500">(opcional — NT 2018.005)</span>
                            </p>
                            <p className="text-xs text-slate-500 mb-4">
                              Quando preenchido, adiciona o bloco <code>&lt;infRespTec&gt;</code> no XML. Alguns estados/operações exigem.
                              Em <strong>SP-Produção com Simples Nacional</strong>, geralmente deve ficar <strong>em branco</strong> (validador legado rejeita).
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>CNPJ do Responsável Técnico</Label>
                                <Input
                                  value={fiscalConfig.respTecCnpj}
                                  onChange={(e) => setFiscalConfig({...fiscalConfig, respTecCnpj: e.target.value})}
                                  placeholder="14 dígitos (apenas números)"
                                  maxLength={18}
                                  data-testid="input-resp-tec-cnpj"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label>Nome do Contato</Label>
                                <Input
                                  value={fiscalConfig.respTecContato}
                                  onChange={(e) => setFiscalConfig({...fiscalConfig, respTecContato: e.target.value})}
                                  placeholder="Nome do responsável"
                                  maxLength={60}
                                  data-testid="input-resp-tec-contato"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label>Email</Label>
                                <Input
                                  type="email"
                                  value={fiscalConfig.respTecEmail}
                                  onChange={(e) => setFiscalConfig({...fiscalConfig, respTecEmail: e.target.value})}
                                  placeholder="contato@empresa.com.br"
                                  maxLength={60}
                                  data-testid="input-resp-tec-email"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label>Telefone</Label>
                                <Input
                                  value={fiscalConfig.respTecFone}
                                  onChange={(e) => setFiscalConfig({...fiscalConfig, respTecFone: e.target.value})}
                                  placeholder="6 a 14 dígitos (DDD + número)"
                                  maxLength={14}
                                  data-testid="input-resp-tec-fone"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          </div>

                          
                          <Separator />
                          
                          {/* Resumo das configurações herdadas */}
                          <div className="bg-slate-50 rounded-lg p-4">
                            <p className="font-semibold text-slate-700 mb-3">Configurações Herdadas (compartilhadas com NFC-e)</p>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-600">Empresa:</span>
                                <span className="font-medium text-slate-800">
                                  {empresaConfig.razaoSocial || 'Não configurado'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Key className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-600">Certificado:</span>
                                <span className={`font-medium ${certificado ? 'text-green-600' : 'text-amber-600'}`}>
                                  {certificado ? 'Configurado' : 'Não configurado'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-600">CNPJ:</span>
                                <span className="font-medium text-slate-800">
                                  {empresaConfig.cnpj || 'Não configurado'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Store className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-600">UF:</span>
                                <span className="font-medium text-slate-800">
                                  {empresaConfig.uf || 'SP'}
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-slate-500 mt-3">
                              Para alterar estes dados, acesse as respectivas abas de configuração.
                            </p>
                          </div>
                          
                          {/* Informações adicionais */}
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                            <h4 className="font-semibold text-emerald-800 mb-2">Quando usar NF-e?</h4>
                            <ul className="text-sm text-emerald-700 space-y-1">
                              <li>• Vendas para outras empresas (B2B)</li>
                              <li>• Vendas de alto valor que exigem documento mais detalhado</li>
                              <li>• Quando o cliente PJ solicita nota fiscal modelo 55</li>
                            </ul>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* TAB: Impressão/DANFE */}
                {fiscalTab === 'danfe' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Printer className="w-5 h-5 text-amber-500" />
                        Configurações de Impressão e DANFE
                      </CardTitle>
                      <CardDescription>Configure a impressão dos documentos fiscais</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Formato do DANFE</Label>
                          <select
                            value={fiscalConfig.formatoDanfe}
                            onChange={(e) => setFiscalConfig({...fiscalConfig, formatoDanfe: e.target.value})}
                            className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                          >
                            <option value="1">Retrato (Padrão)</option>
                            <option value="2">Paisagem</option>
                          </select>
                        </div>
                        
                        <div>
                          <Label>Impressora Padrão</Label>
                          <Input
                            value={fiscalConfig.impressoraPadrao}
                            onChange={(e) => setFiscalConfig({...fiscalConfig, impressoraPadrao: e.target.value})}
                            placeholder="Nome da impressora"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <p className="font-medium">Imprimir Automaticamente</p>
                            <p className="text-sm text-slate-500">Imprimir DANFE ao emitir nota fiscal</p>
                          </div>
                          <Checkbox 
                            checked={fiscalConfig.imprimirAutomatico}
                            onCheckedChange={(checked) => setFiscalConfig({...fiscalConfig, imprimirAutomatico: checked})}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <p className="font-medium">Incluir Logo no DANFE</p>
                            <p className="text-sm text-slate-500">Exibir logo da empresa no documento</p>
                          </div>
                          <Checkbox 
                            checked={fiscalConfig.logoDanfe}
                            onCheckedChange={(checked) => setFiscalConfig({...fiscalConfig, logoDanfe: checked})}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* TAB: Servidor SEFAZ */}
                {fiscalTab === 'sefaz' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Server className="w-5 h-5 text-amber-500" />
                        Servidor SEFAZ e Contingência
                      </CardTitle>
                      <CardDescription>Configurações avançadas de comunicação com a SEFAZ</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Status de Conexão */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="font-medium">Servidor NFC-e</span>
                          </div>
                          <p className="text-sm text-slate-600">
                            {fiscalConfig.ambienteNfce === '1' ? 'Produção' : 'Homologação'} - {fiscalConfig.uf}
                          </p>
                        </div>
                        
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="font-medium">Servidor NF-e</span>
                          </div>
                          <p className="text-sm text-slate-600">
                            {fiscalConfig.ambienteNfe === '1' ? 'Produção' : 'Homologação'} - {fiscalConfig.uf}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <Label>Timeout de Conexão (segundos)</Label>
                        <Input
                          type="number"
                          value={fiscalConfig.timeout}
                          onChange={(e) => setFiscalConfig({...fiscalConfig, timeout: e.target.value})}
                          placeholder="30"
                          className="mt-1 w-48"
                        />
                      </div>
                      
                      <Separator />
                      
                      {/* Contingência */}
                      <div>
                        <Label className="text-base font-semibold">Modo de Contingência</Label>
                        <p className="text-sm text-slate-500 mb-4">Use quando a SEFAZ estiver fora do ar</p>
                        
                        <div className="flex items-center justify-between p-4 border rounded-lg mb-4">
                          <div>
                            <p className="font-medium">Ativar Contingência</p>
                            <p className="text-sm text-slate-500">Emitir notas em modo offline</p>
                          </div>
                          <Checkbox 
                            checked={fiscalConfig.modoContingencia}
                            onCheckedChange={(checked) => setFiscalConfig({...fiscalConfig, modoContingencia: checked})}
                          />
                        </div>
                        
                        {fiscalConfig.modoContingencia && (
                          <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <div>
                              <Label>Tipo de Contingência</Label>
                              <select
                                value={fiscalConfig.tipoContingencia}
                                onChange={(e) => setFiscalConfig({...fiscalConfig, tipoContingencia: e.target.value})}
                                className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                              >
                                <option value="off">Desativado</option>
                                <option value="FS-DA">FS-DA (Formulário de Segurança)</option>
                                <option value="EPEC">EPEC (Evento Prévio)</option>
                                <option value="SVC-AN">SVC-AN (Contingência Nacional)</option>
                                <option value="SVC-RS">SVC-RS (Contingência RS)</option>
                              </select>
                            </div>
                            
                            <div>
                              <Label>Justificativa da Contingência</Label>
                              <Input
                                value={fiscalConfig.justificativaContingencia}
                                onChange={(e) => setFiscalConfig({...fiscalConfig, justificativaContingencia: e.target.value})}
                                placeholder="Ex: Servidor SEFAZ fora do ar"
                                className="mt-1"
                              />
                              <p className="text-xs text-amber-700 mt-1">Mínimo 15 caracteres</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* TAB: Diagnóstico de Números */}
                {fiscalTab === 'diagnostico' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        Diagnóstico de NFC-e
                      </CardTitle>
                      <CardDescription>Verifique configurações e teste a conexão com a SEFAZ</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Testar Configuração e SEFAZ */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-3">Teste de Configuração</h4>
                        <Button
                          onClick={async () => {
                            try {
                              setLoadingAnalise(true);
                              const response = await fetch(`${BACKEND_URL}/api/nfce/status-sefaz`, {
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                              });
                              const data = await response.json();
                              if (response.ok) {
                                // Mostrar resultado
                                const config = data.configuracao;
                                const sefaz = data.sefaz;
                                
                                if (config.pode_emitir) {
                                  if (sefaz.disponivel) {
                                    toast.success('✅ Configuração OK e SEFAZ operacional!');
                                  } else {
                                    toast.warning(`⚠️ Configuração OK, mas SEFAZ: ${sefaz.mensagem || 'indisponível'}`);
                                  }
                                } else {
                                  let problemas = [];
                                  if (config.certificado.status !== 'OK') problemas.push('Certificado');
                                  if (config.csc.status !== 'OK') problemas.push('CSC');
                                  if (config.empresa.status !== 'OK') problemas.push('Dados da Empresa');
                                  toast.error(`❌ Configuração incompleta: ${problemas.join(', ')}`);
                                }
                                
                                // Atualizar estado para mostrar detalhes
                                setAnaliseNumeros(prev => ({
                                  ...prev,
                                  statusSefaz: data
                                }));
                              } else {
                                toast.error(data.detail || 'Erro ao testar');
                              }
                            } catch (error) {
                              toast.error('Erro ao testar configuração');
                            } finally {
                              setLoadingAnalise(false);
                            }
                          }}
                          disabled={loadingAnalise}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {loadingAnalise ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Testando...
                            </>
                          ) : (
                            <>
                              <Server className="w-4 h-4 mr-2" />
                              Testar Configuração e SEFAZ
                            </>
                          )}
                        </Button>
                        
                        {/* Resultado do teste */}
                        {analiseNumeros?.statusSefaz && (
                          <div className="mt-4 space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                              <div className={`p-2 rounded text-center text-sm ${analiseNumeros.statusSefaz.configuracao?.certificado?.status === 'OK' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                <p className="font-medium">Certificado</p>
                                <p>{analiseNumeros.statusSefaz.configuracao?.certificado?.status}</p>
                              </div>
                              <div className={`p-2 rounded text-center text-sm ${analiseNumeros.statusSefaz.configuracao?.csc?.status === 'OK' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                <p className="font-medium">CSC</p>
                                <p>{analiseNumeros.statusSefaz.configuracao?.csc?.status}</p>
                                {analiseNumeros.statusSefaz.configuracao?.csc?.status !== 'OK' && (
                                  <p className="text-[11px] mt-1">
                                    {!analiseNumeros.statusSefaz.configuracao?.csc?.csc_token && 'Falta o CSC (Token). '}
                                    {!analiseNumeros.statusSefaz.configuracao?.csc?.csc_id_ok && 'Falta o ID do CSC. '}
                                  </p>
                                )}
                              </div>
                              <div className={`p-2 rounded text-center text-sm ${analiseNumeros.statusSefaz.configuracao?.empresa?.status === 'OK' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                <p className="font-medium">Empresa</p>
                                <p>{analiseNumeros.statusSefaz.configuracao?.empresa?.status}</p>
                              </div>
                            </div>
                            <div className={`p-2 rounded text-center ${analiseNumeros.statusSefaz.sefaz?.disponivel ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              <p className="font-medium">SEFAZ: {analiseNumeros.statusSefaz.sefaz?.disponivel ? 'Operacional' : analiseNumeros.statusSefaz.sefaz?.mensagem || 'Indisponível'}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <Separator />
                      
                      {/* Botão Analisar Numeração */}
                      <div className="flex gap-4">
                        <Button
                          onClick={async () => {
                            try {
                              setLoadingAnalise(true);
                              const response = await fetch(`${BACKEND_URL}/api/nfce/numeros/analise`, {
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                              });
                              const data = await response.json();
                              if (response.ok) {
                                setAnaliseNumeros(prev => ({ ...prev, ...data }));
                                toast.success('Análise concluída!');
                              } else {
                                toast.error(data.detail || 'Erro ao analisar');
                              }
                            } catch (error) {
                              toast.error('Erro ao analisar números');
                            } finally {
                              setLoadingAnalise(false);
                            }
                          }}
                          disabled={loadingAnalise}
                          variant="outline"
                        >
                          {loadingAnalise ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analisando...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Analisar Numeração
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {/* Resultado da Análise */}
                      {analiseNumeros && (
                        <div className="space-y-4">
                          {/* Resumo */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="p-4 bg-blue-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-blue-700">{analiseNumeros.proximo_numero_configurado}</p>
                              <p className="text-sm text-blue-600">Próximo Número Configurado</p>
                            </div>
                            <div className="p-4 bg-green-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-green-700">{analiseNumeros.numeros_autorizados}</p>
                              <p className="text-sm text-green-600">Notas Autorizadas</p>
                            </div>
                            <div className="p-4 bg-amber-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-amber-700">{analiseNumeros.numeros_simulacao?.length || 0}</p>
                              <p className="text-sm text-amber-600">Notas em Simulação</p>
                            </div>
                            <div className="p-4 bg-rose-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-rose-700">{analiseNumeros.numeros_pulados?.length || 0}</p>
                              <p className="text-sm text-rose-600">Números Pulados</p>
                            </div>
                          </div>
                          
                          {/* Detalhes */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-slate-50 rounded-lg">
                              <h4 className="font-semibold mb-2">Intervalo de Números</h4>
                              <p className="text-sm text-slate-600">
                                Menor número: <strong>{analiseNumeros.menor_numero_usado || 'N/A'}</strong>
                              </p>
                              <p className="text-sm text-slate-600">
                                Maior número: <strong>{analiseNumeros.maior_numero_usado || 'N/A'}</strong>
                              </p>
                              <p className="text-sm text-slate-600">
                                Sugestão próximo: <strong>{analiseNumeros.sugestao_proximo_numero}</strong>
                              </p>
                            </div>
                            
                            {analiseNumeros.numeros_simulacao?.length > 0 && (
                              <div className="p-4 bg-amber-50 rounded-lg">
                                <h4 className="font-semibold mb-2 text-amber-800">Números em Simulação</h4>
                                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                                  {analiseNumeros.numeros_simulacao.slice(0, 20).map(num => (
                                    <span key={num} className="px-2 py-1 bg-amber-200 text-amber-800 rounded text-xs font-mono">
                                      {num}
                                    </span>
                                  ))}
                                  {analiseNumeros.numeros_simulacao.length > 20 && (
                                    <span className="px-2 py-1 text-amber-600 text-xs">
                                      +{analiseNumeros.numeros_simulacao.length - 20} mais
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Números Pulados */}
                          {analiseNumeros.numeros_pulados?.length > 0 && (
                            <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
                              <h4 className="font-semibold mb-2 text-rose-800">Números Pulados (Gap na Sequência)</h4>
                              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                                {analiseNumeros.numeros_pulados.slice(0, 30).map(num => (
                                  <span key={num} className="px-2 py-1 bg-rose-200 text-rose-800 rounded text-xs font-mono">
                                    {num}
                                  </span>
                                ))}
                                {analiseNumeros.numeros_pulados.length > 30 && (
                                  <span className="px-2 py-1 text-rose-600 text-xs">
                                    +{analiseNumeros.numeros_pulados.length - 30} mais
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-rose-600 mt-2">
                                Estes números foram pulados e podem precisar ser inutilizados na SEFAZ.
                              </p>
                            </div>
                          )}
                          
                          <Separator />
                          
                          {/* Ações de Correção */}
                          <div className="space-y-4">
                            <h4 className="font-semibold">Ações de Correção</h4>
                            
                            {/* Corrigir próximo número automaticamente */}
                            <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                              <div className="flex-1">
                                <p className="font-medium text-blue-800">Corrigir próximo número automaticamente</p>
                                <p className="text-sm text-blue-600">
                                  Define o próximo número como {analiseNumeros.sugestao_proximo_numero} (baseado no maior número usado + 1)
                                </p>
                              </div>
                              <Button
                                onClick={async () => {
                                  try {
                                    setCorrigindoNumeros(true);
                                    const response = await fetch(`${BACKEND_URL}/api/nfce/numeros/corrigir`, {
                                      method: 'POST',
                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    const data = await response.json();
                                    if (response.ok) {
                                      toast.success(data.mensagem || 'Número corrigido!');
                                      // Recarregar análise
                                      setFiscalConfig(prev => ({ ...prev, proximoNumeroNfce: String(data.novo_proximo_numero) }));
                                    } else {
                                      toast.error(data.detail || 'Erro ao corrigir');
                                    }
                                  } catch (error) {
                                    toast.error('Erro ao corrigir números');
                                  } finally {
                                    setCorrigindoNumeros(false);
                                  }
                                }}
                                disabled={corrigindoNumeros}
                                variant="outline"
                                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                              >
                                {corrigindoNumeros ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Corrigir Automático'}
                              </Button>
                            </div>
                            
                            {/* Definir número manualmente */}
                            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                              <div className="flex-1">
                                <p className="font-medium">Definir próximo número manualmente</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Input
                                    type="number"
                                    value={novoProximoNumero}
                                    onChange={(e) => setNovoProximoNumero(e.target.value)}
                                    placeholder="Ex: 150"
                                    className="w-32"
                                    min="1"
                                  />
                                  <Button
                                    onClick={async () => {
                                      if (!novoProximoNumero || parseInt(novoProximoNumero) < 1) {
                                        toast.error('Informe um número válido');
                                        return;
                                      }
                                      try {
                                        setCorrigindoNumeros(true);
                                        const response = await fetch(`${BACKEND_URL}/api/nfce/numeros/corrigir?novo_numero=${novoProximoNumero}`, {
                                          method: 'POST',
                                          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                        });
                                        const data = await response.json();
                                        if (response.ok) {
                                          toast.success(`Próximo número definido como ${novoProximoNumero}`);
                                          setFiscalConfig(prev => ({ ...prev, proximoNumeroNfce: novoProximoNumero }));
                                          setNovoProximoNumero('');
                                        } else {
                                          toast.error(data.detail || 'Erro ao definir número');
                                        }
                                      } catch (error) {
                                        toast.error('Erro ao definir número');
                                      } finally {
                                        setCorrigindoNumeros(false);
                                      }
                                    }}
                                    disabled={corrigindoNumeros || !novoProximoNumero}
                                    size="sm"
                                  >
                                    Definir
                                  </Button>
                                </div>
                              </div>
                            </div>
                            
                            {/* Limpar simulações */}
                            {analiseNumeros.numeros_simulacao?.length > 0 && (
                              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                                <div className="flex-1">
                                  <p className="font-medium text-amber-800">Limpar números de simulação</p>
                                  <p className="text-sm text-amber-600">
                                    Remove os números das vendas em simulação para que possam ser reutilizados. 
                                    Use apenas se você NÃO pretende reenviar essas notas para a SEFAZ.
                                  </p>
                                </div>
                                <Button
                                  onClick={async () => {
                                    if (!window.confirm('Tem certeza? Isso vai remover os números das vendas em simulação. Esta ação não pode ser desfeita.')) {
                                      return;
                                    }
                                    try {
                                      setLimpandoSimulacao(true);
                                      const response = await fetch(`${BACKEND_URL}/api/nfce/numeros/limpar-simulacao`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                      });
                                      const data = await response.json();
                                      if (response.ok) {
                                        toast.success(data.mensagem || 'Simulações limpas!');
                                        // Recarregar análise
                                        setAnaliseNumeros(null);
                                      } else {
                                        toast.error(data.detail || 'Erro ao limpar');
                                      }
                                    } catch (error) {
                                      toast.error('Erro ao limpar simulações');
                                    } finally {
                                      setLimpandoSimulacao(false);
                                    }
                                  }}
                                  disabled={limpandoSimulacao}
                                  variant="outline"
                                  className="border-amber-500 text-amber-700 hover:bg-amber-100"
                                >
                                  {limpandoSimulacao ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Limpar Simulações'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Informações */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-2">Sobre o Diagnóstico</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• Notas em <strong>simulação</strong> foram emitidas sem comunicação com a SEFAZ (modo teste ou configuração pendente)</li>
                          <li>• Notas em simulação podem ser reenviadas na página de <strong>Vendas</strong></li>
                          <li>• <strong>Números pulados</strong> devem ser inutilizados na SEFAZ para evitar rejeições futuras</li>
                          <li>• Ao reenviar uma nota simulada, o sistema reutiliza o número original automaticamente</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Tab Diagnóstico NF-e */}
                {fiscalTab === 'diagnostico_nfe' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-500" />
                        Diagnóstico de NF-e (Modelo 55)
                      </CardTitle>
                      <CardDescription>Verifique a configuração do certificado e teste a conexão com a SEFAZ para NF-e</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Testar Certificado e SEFAZ NF-e */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-3">Verificar Certificado e Serviço SEFAZ</h4>
                        <p className="text-sm text-blue-600 mb-4">
                          Este teste valida se o certificado digital está configurado corretamente e se a comunicação com a SEFAZ está funcionando.
                        </p>
                        <Button
                          onClick={async () => {
                            try {
                              setLoadingStatusNfe(true);
                              setStatusSefazNfe(null);
                              const response = await fetch(`${BACKEND_URL}/api/nfe/verificar-sefaz`, {
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                              });
                              const data = await response.json();
                              setStatusSefazNfe(data);
                              
                              if (data.sucesso && data.online) {
                                toast.success('✅ Certificado válido e SEFAZ operacional!');
                              } else if (data.certificado_valido && !data.online) {
                                toast.warning(`⚠️ Certificado OK, mas SEFAZ: ${data.motivo || data.erro}`);
                              } else {
                                toast.error(`❌ ${data.erro || 'Erro na verificação'}`);
                              }
                            } catch (error) {
                              toast.error('Erro ao verificar status da SEFAZ');
                              setStatusSefazNfe({ sucesso: false, erro: 'Erro de conexão' });
                            } finally {
                              setLoadingStatusNfe(false);
                            }
                          }}
                          disabled={loadingStatusNfe}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {loadingStatusNfe ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Verificando...
                            </>
                          ) : (
                            <>
                              <Server className="w-4 h-4 mr-2" />
                              Verificar Certificado e SEFAZ
                            </>
                          )}
                        </Button>
                        
                        {/* Resultado da verificação */}
                        {statusSefazNfe && (
                          <div className="mt-4 space-y-3">
                            {/* Status Geral */}
                            <div className={`p-4 rounded-lg ${
                              statusSefazNfe.sucesso && statusSefazNfe.online 
                                ? 'bg-green-100 border border-green-300' 
                                : statusSefazNfe.certificado_valido 
                                  ? 'bg-amber-100 border border-amber-300'
                                  : 'bg-red-100 border border-red-300'
                            }`}>
                              <div className="flex items-center gap-2 mb-2">
                                {statusSefazNfe.sucesso && statusSefazNfe.online ? (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : statusSefazNfe.certificado_valido ? (
                                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-red-600" />
                                )}
                                <span className={`font-semibold ${
                                  statusSefazNfe.sucesso && statusSefazNfe.online 
                                    ? 'text-green-800' 
                                    : statusSefazNfe.certificado_valido 
                                      ? 'text-amber-800'
                                      : 'text-red-800'
                                }`}>
                                  {statusSefazNfe.sucesso && statusSefazNfe.online 
                                    ? 'Tudo certo! Pronto para emitir NF-e real' 
                                    : statusSefazNfe.certificado_valido 
                                      ? 'Certificado OK, mas há um problema com a SEFAZ'
                                      : 'Problema na configuração'}
                                </span>
                              </div>
                              
                              {statusSefazNfe.erro && (
                                <p className="text-sm mt-1">{statusSefazNfe.erro}</p>
                              )}
                              
                              {/* Lista de problemas se houver */}
                              {statusSefazNfe.problemas && statusSefazNfe.problemas.length > 0 && (
                                <ul className="text-sm mt-2 list-disc list-inside">
                                  {statusSefazNfe.problemas.map((problema, idx) => (
                                    <li key={idx}>{problema}</li>
                                  ))}
                                </ul>
                              )}
                              
                              {/* Info do certificado incompleto */}
                              {statusSefazNfe.codigo === 'CERT_INCOMPLETO' && statusSefazNfe.certificado_info && (
                                <div className="text-sm mt-2 p-2 bg-red-50 rounded">
                                  <p>Tamanho atual: <strong>{statusSefazNfe.certificado_info.tamanho_atual}</strong> caracteres</p>
                                  <p>Tamanho mínimo esperado: <strong>{statusSefazNfe.certificado_info.tamanho_minimo_esperado}</strong> caracteres</p>
                                </div>
                              )}
                              
                              {statusSefazNfe.motivo && statusSefazNfe.codigo && (
                                <p className="text-sm mt-1">
                                  <strong>Código {statusSefazNfe.codigo}:</strong> {statusSefazNfe.motivo}
                                </p>
                              )}
                            </div>
                            
                            {/* Grid de Status */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div className={`p-3 rounded-lg text-center ${statusSefazNfe.certificado_valido ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                <p className={`font-medium text-sm ${statusSefazNfe.certificado_valido ? 'text-green-700' : 'text-red-700'}`}>
                                  Certificado
                                </p>
                                <p className={`text-xs ${statusSefazNfe.certificado_valido ? 'text-green-600' : 'text-red-600'}`}>
                                  {statusSefazNfe.certificado_valido ? '✅ Válido' : '❌ Inválido'}
                                </p>
                              </div>
                              
                              <div className={`p-3 rounded-lg text-center ${statusSefazNfe.online ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                                <p className={`font-medium text-sm ${statusSefazNfe.online ? 'text-green-700' : 'text-amber-700'}`}>
                                  SEFAZ
                                </p>
                                <p className={`text-xs ${statusSefazNfe.online ? 'text-green-600' : 'text-amber-600'}`}>
                                  {statusSefazNfe.online ? '✅ Online' : '⚠️ Indisponível'}
                                </p>
                              </div>
                              
                              <div className="p-3 rounded-lg text-center bg-slate-50 border border-slate-200">
                                <p className="font-medium text-sm text-slate-700">Ambiente</p>
                                <p className="text-xs text-slate-600">
                                  {statusSefazNfe.ambiente === 'producao' ? '🔴 Produção' : '🟡 Homologação'}
                                </p>
                              </div>
                              
                              <div className="p-3 rounded-lg text-center bg-slate-50 border border-slate-200">
                                <p className="font-medium text-sm text-slate-700">UF</p>
                                <p className="text-xs text-slate-600">{statusSefazNfe.uf || 'N/A'}</p>
                              </div>
                            </div>
                            
                            {/* Informações do Certificado */}
                            {statusSefazNfe.certificado_info && Object.keys(statusSefazNfe.certificado_info).length > 0 && (
                              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                                <h5 className="font-semibold text-slate-800 mb-2">Informações do Certificado</h5>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {statusSefazNfe.certificado_info.cn && (
                                    <div>
                                      <span className="text-slate-500">Titular:</span>
                                      <span className="ml-2 text-slate-700 font-medium">{statusSefazNfe.certificado_info.cn}</span>
                                    </div>
                                  )}
                                  {statusSefazNfe.certificado_info.validade_fim && (
                                    <div>
                                      <span className="text-slate-500">Validade:</span>
                                      <span className={`ml-2 font-medium ${statusSefazNfe.certificado_info.expirado ? 'text-red-600' : 'text-slate-700'}`}>
                                        {statusSefazNfe.certificado_info.validade_fim}
                                      </span>
                                    </div>
                                  )}
                                  {statusSefazNfe.certificado_info.dias_restantes !== undefined && (
                                    <div>
                                      <span className="text-slate-500">Dias restantes:</span>
                                      <span className={`ml-2 font-medium ${
                                        statusSefazNfe.certificado_info.dias_restantes < 0 
                                          ? 'text-red-600' 
                                          : statusSefazNfe.certificado_info.dias_restantes < 30 
                                            ? 'text-amber-600' 
                                            : 'text-green-600'
                                      }`}>
                                        {statusSefazNfe.certificado_info.dias_restantes < 0 
                                          ? `Expirado há ${Math.abs(statusSefazNfe.certificado_info.dias_restantes)} dias`
                                          : `${statusSefazNfe.certificado_info.dias_restantes} dias`}
                                      </span>
                                    </div>
                                  )}
                                  {statusSefazNfe.tempo_resposta_ms && (
                                    <div>
                                      <span className="text-slate-500">Tempo de resposta:</span>
                                      <span className="ml-2 text-slate-700">{statusSefazNfe.tempo_resposta_ms}ms</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Debug info - URL consultada */}
                            {(statusSefazNfe.url_consultada || statusSefazNfe.debug) && (
                              <div className="p-3 bg-gray-100 rounded-lg border border-gray-200 text-xs font-mono">
                                <p className="text-gray-600 mb-1"><strong>Debug:</strong></p>
                                {statusSefazNfe.url_consultada && (
                                  <p className="text-gray-500 break-all">URL: {statusSefazNfe.url_consultada}</p>
                                )}
                                {statusSefazNfe.debug && (
                                  <>
                                    <p className="text-gray-500">UF: {statusSefazNfe.debug.uf}, Ambiente: {statusSefazNfe.debug.ambiente}</p>
                                    <p className="text-gray-500">HTTP Status: {statusSefazNfe.debug.http_status}</p>
                                  </>
                                )}
                                {statusSefazNfe.resposta_raw && (
                                  <details className="mt-2">
                                    <summary className="cursor-pointer text-gray-600">Ver resposta raw</summary>
                                    <pre className="mt-1 p-2 bg-gray-200 rounded overflow-auto max-h-40 text-xs">{statusSefazNfe.resposta_raw}</pre>
                                  </details>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <Separator />
                      
                      {/* Informações */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-2">Sobre a Verificação</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• O teste verifica se o <strong>certificado digital</strong> está configurado e válido</li>
                          <li>• Também verifica se a <strong>comunicação com a SEFAZ</strong> está funcionando</li>
                          <li>• Se o certificado for inválido ou expirado, as NF-e serão emitidas em <strong>modo simulação</strong></li>
                          <li>• Notas em simulação <strong>NÃO têm valor fiscal</strong> e precisam ser reenviadas após corrigir o certificado</li>
                          <li>• Verifique se o <strong>ambiente</strong> está correto: Homologação (testes) ou Produção (notas reais)</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Botão Salvar */}
                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Salvar Configurações Fiscais
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Impressora Config */}
            {activeTab === 'impressora' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Printer className="w-5 h-5 text-amber-500" />
                    Configurações da Impressora
                  </CardTitle>
                  <CardDescription>Configure a impressora térmica para cupons</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-100 rounded-lg p-8 text-center">
                    <Printer className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Em desenvolvimento</p>
                    <p className="text-sm text-slate-500 mt-1">A configuração da impressora estará disponível em breve</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Instalar App */}
            {activeTab === 'app' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Download className="w-5 h-5 text-amber-500" />
                      Instalar nfcesysPdv
                    </CardTitle>
                    <CardDescription>
                      Instale o aplicativo no seu computador, tablet ou celular para acesso rápido e offline
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <InstallPWA variant="card" />
                  </CardContent>
                </Card>

                {/* Dispositivos Suportados */}
                <Card>
                  <CardHeader>
                    <CardTitle>Dispositivos Compatíveis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-slate-50 rounded-xl text-center">
                        <Laptop className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <h4 className="font-semibold text-slate-900">Computador</h4>
                        <p className="text-sm text-slate-500 mt-1">Windows, Mac ou Linux</p>
                        <p className="text-xs text-slate-400 mt-2">Chrome, Edge, Firefox</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl text-center">
                        <Tablet className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <h4 className="font-semibold text-slate-900">Tablet</h4>
                        <p className="text-sm text-slate-500 mt-1">iPad, Android Tablet</p>
                        <p className="text-xs text-slate-400 mt-2">Safari, Chrome</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl text-center">
                        <Smartphone className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <h4 className="font-semibold text-slate-900">Celular</h4>
                        <p className="text-sm text-slate-500 mt-1">iPhone, Android</p>
                        <p className="text-xs text-slate-400 mt-2">Safari, Chrome</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Instruções */}
                <Card>
                  <CardHeader>
                    <CardTitle>Como Instalar</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <h4 className="font-semibold text-amber-800 mb-2">No Chrome/Edge (Computador)</h4>
                      <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                        <li>Clique no ícone de instalação (⊕) na barra de endereços</li>
                        <li>Ou clique nos 3 pontos (⋮) → "Instalar nfcesysPdv"</li>
                        <li>Clique em "Instalar" para confirmar</li>
                      </ol>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <h4 className="font-semibold text-blue-800 mb-2">No iPhone/iPad (Safari)</h4>
                      <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                        <li>Toque no botão de compartilhar (ícone de quadrado com seta)</li>
                        <li>Role para baixo e toque em "Adicionar à Tela de Início"</li>
                        <li>Toque em "Adicionar" para confirmar</li>
                      </ol>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <h4 className="font-semibold text-green-800 mb-2">No Android (Chrome)</h4>
                      <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
                        <li>Toque nos 3 pontos (⋮) no canto superior direito</li>
                        <li>Toque em "Instalar aplicativo" ou "Adicionar à tela inicial"</li>
                        <li>Toque em "Instalar" para confirmar</li>
                      </ol>
                    </div>
                  </CardContent>
                </Card>

                {/* Benefícios */}
                <Card>
                  <CardHeader>
                    <CardTitle>Benefícios do App Instalado</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Check className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">Acesso Rápido</h4>
                          <p className="text-sm text-slate-500">Ícone na área de trabalho ou tela inicial</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Monitor className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">Tela Cheia</h4>
                          <p className="text-sm text-slate-500">Interface sem barras do navegador</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Wifi className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">Funciona Offline</h4>
                          <p className="text-sm text-slate-500">Continue usando mesmo sem internet</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Bell className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">Notificações</h4>
                          <p className="text-sm text-slate-500">Receba alertas de novos pedidos</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracoesPage;
